import { FC, useState, useMemo, useRef, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, TextField, MenuItem, IconButton,
  Grid, CircularProgress, Avatar, InputAdornment, Menu, Popover,
  useMediaQuery, useTheme, Tooltip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { alpha, darken, lighten } from '@mui/material/styles';
import { 
  ChevronLeft, ChevronRight,
  Search, Close, ExpandMore, Receipt as ReceiptIcon,
  Warning as WarningIcon,
  EventBusy as EventBusyIcon, Delete as DeleteIcon, Edit as EditIcon,
  CheckCircle as CheckCircleIcon, Warehouse as WarehouseIcon, Add as AddIcon, LoginOutlined as LoginIcon,
  PlaceOutlined as PlaceIcon, PersonAddAlt1Outlined as PersonAddIcon, PersonOutline as OwnerIcon, SettingsOutlined
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import format from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import { todayLocalISO } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { appointmentApi } from '../../utils/api';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import api from '../../utils/api';
import { withOfflineFallback, readAppointmentsInRange, readTimeBlocks, readVeterinarians, executeUpdate, executeDelete, OfflineWriteBlockedError, skipWhenOffline, useIsOffline } from '../../offline';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import AddAppointmentDialog from './AddAppointmentDialog';
import AddTimeBlockDialog from './AddTimeBlockDialog';
import { getAppointmentSubject } from './subjectLabel';
import AppointmentHoverCard, { resolveHomeBarnId, resolveVisitBarnId } from './AppointmentHoverCard';
import { getWeekDays, getMonthGridDays, providerInitials, countByDayAndProvider, getAppointmentColor } from './calendarGrid';
import { barnsService } from '../../services/barnsService';
import { appointmentService } from '../../services/appointmentService';
import type { Appointment, Veterinarian } from './types';
import { AppointmentPanel, AppointmentEditor, EditForm } from './AppointmentPanel';
import { AppointmentStatus, normalizeStatus, statusStyle, isCancelled } from './appointmentStatus';
import CalendarSettings, { useSchedulePreferences } from './CalendarSettings';
import EquipmentDayGrid, { buildEquipmentReservations, EquipmentWeekGrid, EquipmentReservation } from './EquipmentDayGrid';
import type { Barn } from '../../services/barnsService';
import EquipmentWindowsPicker, { buildEquipmentPayload, isAllDayWindow } from '../../components/EquipmentWindowsPicker';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useRBAC } from '../../auth/useRBAC';
import { scheduleControls } from '../../auth/pageControls';
import { SCREEN_ACCESS, canViewScreen } from '../../auth/screenAccess';
import { 
  DndContext, 
  DragEndEvent, 
  DragStartEvent,
  useSensor, 
  useSensors, 
  PointerSensor, 
  TouchSensor,
  KeyboardSensor,
  closestCenter,
  CollisionDetection,
  useDraggable,
  useDroppable
} from '@dnd-kit/core';

// Time intervals in minutes
type TimeInterval = 15 | 30 | 60;

// View types
type ViewType = 'daily' | 'weekly' | 'monthly';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// One tint per vet, reused by index so a vet keeps their colour across the week and month views.
/** The Unassigned column's provider. */
const UNASSIGNED_PROVIDER = { id: 'unassigned', name: 'Unassigned', professionalTitle: 'No Vet Assigned' };

/** A booking for the barn itself, with no horses: it looks like a barn visit and may move to another barn. */
const isBarnOnlyBooking = (apt: Appointment) => !apt.visitGroupId && !apt.horseId && !apt.clientId && !!(apt.barnId || apt.barn);

const PROVIDER_COLORS = [
  { bg: '#DCE8F8', fg: '#1E4785' },
  { bg: '#DFF1E6', fg: '#1D5C38' },
  { bg: '#ECE5F8', fg: '#4A2E86' },
  { bg: '#FBE9D7', fg: '#7A4314' },
  { bg: '#DDF1F1', fg: '#14595A' },
  { bg: '#FBE4EE', fg: '#82264F' },
];

/**
 * Drop where the block STARTS: measure from a thin strip at the dragged block's top edge, not
 * its centre, or a two-hour block lands up to an hour away from where its top was let go.
 */
const startEdgeCollision: CollisionDetection = (args) =>
  closestCenter({
    ...args,
    collisionRect: { ...args.collisionRect, top: args.collisionRect.top + 2, bottom: args.collisionRect.top + 3, height: 1 },
  });

// Segmented controls (view, slot size), styled like the canvas.
const segmentedSx = {
  bgcolor: '#F3F4F6',
  p: '3px',
  gap: '2px',
  borderRadius: '10px',
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '8px !important',
    px: 1.75,
    py: 0.5,
    textTransform: 'none',
    fontWeight: 500,
    color: 'text.secondary',
  },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    bgcolor: 'background.paper',
    color: 'text.primary',
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(17,17,17,0.08)',
  },
} as const;

const toolbarIconSx = { width: 36, height: 36, border: '1px solid', borderColor: 'divider', borderRadius: 2 } as const;

/**
 * A block's colours from its type colour (clinic-chosen or default): a light fill, a mid edge and
 * dark text, so every clinic colour stays readable without white text on a strong fill.
 */
const blockPalette = (color: string) => ({
  fill: lighten(color, 0.86),
  line: alpha(color, 0.55),
  ink: darken(color, 0.55),
});

// Helper function to generate time slots for the day
const generateTimeSlots = (interval: TimeInterval, startHour = 6, endHour = 23) => {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    // For the first slot of each hour, always include it regardless of interval
    const timeString = `${hour.toString().padStart(2, '0')}:00`;
    slots.push({
      time: timeString,
      hour,
      minute: 0,
      slotIndex: slots.length,
      isHourMark: true
    });
    
    // Add interval-based slots within the hour (if interval is less than 60 minutes)
    if (interval < 60) {
      for (let minute = interval; minute < 60; minute += interval) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push({
          time: timeString,
          hour,
          minute,
          slotIndex: slots.length,
          isHourMark: false
        });
      }
    }
  }
  return slots;
};


// Format duration in a human-readable way
const formatDuration = (minutes?: number): string => {
  if (!minutes) return '1 hour'; // Default fallback
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${minutes} min`;
  } else if (remainingMinutes === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
};


const fetchAppointments = async (params?: {
  veterinarianId?: string;
  dateFrom?: string;
  dateTo?: string;
}) => {
  try {
    return await appointmentApi.list(params);
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

const fetchVeterinarians = async () => {
  try {
    const response = await api.get('/users/veterinarians');
    return response.data;
  } catch (error) {
    console.error('Error fetching veterinarians:', error);
    throw error;
  }
};

// Droppable time slot component
interface DroppableTimeSlotProps {
  providerId: string;
  hour: number;
  minute: number;
  slotIndex: number;
  timeInterval: TimeInterval;
  isHourMark: boolean;
  onSlotClick: () => void;
  isDragging: boolean;
  date?: Date; // Optional date for weekly view
}

const DroppableTimeSlot: FC<DroppableTimeSlotProps> = ({
  providerId,
  hour,
  minute,
  slotIndex,
  timeInterval,
  isHourMark,
  onSlotClick,
  isDragging,
  date,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot-${providerId}-${hour}-${minute}-${date?.getTime() || 'current'}`,
    data: {
      providerId,
      hour,
      minute,
      date,
    },
  });

  const slotHeight = timeInterval === 15 ? 40 : timeInterval === 30 ? 50 : 60;
  const isUnassignedColumn = providerId === 'unassigned';

  return (
    <Box
      ref={setNodeRef}
      onClick={onSlotClick}
      sx={{
        position: 'absolute',
        top: `${slotIndex * slotHeight}px`,
        left: 0,
        right: 0,
        height: `${slotHeight}px`,
        boxSizing: 'border-box',
        // Hour lines sit at the top of each hour; slots inside the hour get a fainter line.
        borderTop: isHourMark ? '1px solid #E9EBEE' : '1px solid #F6F7F8',
        cursor: 'pointer',
        backgroundColor: isOver ? (isUnassignedColumn ? '#FFF3E0' : '#FCF0E3') : 'transparent',
        '&:hover': {
          backgroundColor: isOver ? (isUnassignedColumn ? '#FFE0B2' : '#F8E3CC') : '#FBF7F2',
        },
        transition: 'background-color 0.15s ease-in-out',
      }}
    />
  );
};

// Calendar blocks. Both share the hover card, the tinted type colours and the drag behaviour.
const hoverTooltipProps = {
  placement: 'right-start' as const,
  enterDelay: 350,
  disableInteractive: true,
  componentsProps: {
    tooltip: {
      sx: {
        p: 0,
        maxWidth: 'none',
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 12px 32px rgba(17,17,17,0.16)',
      },
    },
  },
};

const blockTimeRange = (startISO: string, minutes: number) => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = DateTime.fromISO(startISO, { zone: 'utc' }).setZone(zone);
  const end = start.plus({ minutes });
  const sameHalf = start.toFormat('a') === end.toFormat('a');
  return `${start.toFormat(sameHalf ? 'h:mm' : 'h:mm a')} - ${end.toFormat('h:mm a')}`;
};

/**
 * An equipment window moved with its appointment. An "All day" hold stays all day on the
 * appointment's new day; shifting it by the delta would hold the unit into the next day.
 */
const shiftEquipmentWindow = (
  schedule: { startTime: string; endTime: string },
  deltaMs: number,
  newAppointmentStart: Date,
) => {
  const start = new Date(schedule.startTime);
  const end = new Date(schedule.endTime);
  return isAllDayWindow(start, end)
    ? { startTime: startOfDay(newAppointmentStart), endTime: endOfDay(newAppointmentStart) }
    : { startTime: new Date(start.getTime() + deltaMs), endTime: new Date(end.getTime() + deltaMs) };
};

interface DraggableAppointmentProps {
  appointment: Appointment;
  position: { exactTop: number; height: number };
  onOpenDetails: (appointment: Appointment) => void;
  typeColorMap?: Record<string, string>;
  /**
   * May this person MOVE an appointment by dragging it? A drag lands on the same
   * `PUT /appointments/:id` the edit dialog uses (appointments.controller.ts `update` :148-149),
   * so it needs `manage:appointments` — and it was the one write on this screen with no gate at
   * all, because it is not a BUTTON: a viewer could drag a patient's visit to another slot and
   * watch it snap back on the 403.
   */
  canReschedule: boolean;
  /** The barn this visit goes to, from the clinic's barn list; null when unknown or offline. */
  barn?: Barn | null;
  /** The barn the horse lives at, for a visit at the clinic. */
  homeBarn?: Barn | null;
}

const DraggableAppointment: FC<DraggableAppointmentProps> = ({
  appointment,
  position,
  onOpenDetails,
  typeColorMap,
  canReschedule,
  barn,
  homeBarn,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: appointment.id,
    disabled: !canReschedule,
  });
  const palette = blockPalette(getAppointmentColor(appointment, typeColorMap));
  const status = normalizeStatus(appointment.status);
  const height = position.height - 4;
  const subject = getAppointmentSubject(appointment).name;
  // The owner, on a line of its own under the horse: run together ("Dusty Lynch") it read as the
  // horse's name. It still tells apart three horses called Dusty.
  const owner = appointment.horse && Array.isArray(appointment.horse.owners) ? appointment.horse.owners[0]?.name?.trim() : '';
  const alerts = appointment.horse?.medicalAlerts ?? [];
  const barnName = appointment.barn?.name || barn?.name;
  const equipmentNames = (appointment.equipmentSchedules ?? []).map((es) => es.equipment.name).join(', ');
  // One card for every visit (Ali, 2026-10-19): the same lines in the same order, as many whole lines
  // as the block holds. A block with room for one line shows the horse; its place gives the time.
  const room = height - 10; // 4px padding top and bottom, 1px border
  const order = ['time', 'subject', 'type', ...(owner ? ['owner'] : []), ...(barnName ? ['place'] : []), ...(equipmentNames ? ['equipment'] : [])];
  const shown = new Set<string>();
  let used = 0;
  for (const line of order) {
    const lineHeight = (line === 'subject' ? 17 : 15) + (shown.size ? 2 : 0);
    if (used + lineHeight > room) break;
    used += lineHeight;
    shown.add(line);
  }
  if (!shown.has('subject')) shown.clear();
  const single = shown.size === 0;

  return (
    <Tooltip {...hoverTooltipProps} title={isDragging ? '' : <AppointmentHoverCard appointment={appointment} barn={barn} homeBarn={homeBarn} />}>
      <Box
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`${subject}, ${appointment.type}, ${blockTimeRange(appointment.date, appointment.duration || 60)}${
          status === 'scheduled' ? '' : `, ${statusStyle(status).label}`
        }`}
        onClick={(e) => {
          if (!isDragging) {
            e.stopPropagation();
            onOpenDetails(appointment);
          }
        }}
        sx={{
          position: 'absolute',
          top: `${position.exactTop + 2}px`,
          left: '4px',
          right: '4px',
          height: `${height}px`,
          minHeight: '20px',
          boxSizing: 'border-box',
          px: 1,
          py: single ? 0 : 0.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: single ? 'center' : 'flex-start',
          gap: 0.25,
          overflow: 'hidden',
          bgcolor: palette.fill,
          color: palette.ink,
          // Tentative: not yet confirmed with the owner. No-show: faded, kept for the record.
          border: `1px ${status === 'tentative' ? 'dashed' : 'solid'} ${palette.line}`,
          borderRadius: 2,
          opacity: status === 'no_show' ? 0.55 : 1,
          cursor: canReschedule ? 'grab' : 'pointer',
          zIndex: isDragging ? 1000 : 10,
          boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.18)' : 'none',
          '&:hover': { boxShadow: `0 0 0 2px ${palette.line}` },
          '&:active': { cursor: canReschedule ? 'grabbing' : 'pointer' },
          transition: isDragging ? 'none' : 'box-shadow 0.15s',
          ...(transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.6 : 1 } : {}),
        }}
      >
        {single && (
          <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.2, pointerEvents: 'none' }}>
            {subject}
          </Typography>
        )}
        {shown.has('time') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.72rem', fontWeight: 500, lineHeight: 1.3, pointerEvents: 'none' }}>
              {blockTimeRange(appointment.date, appointment.duration || 60)}
            </Typography>
            {status === 'completed' && (
              <Tooltip title="Completed" arrow>
                <CheckCircleIcon sx={{ fontSize: 14 }} />
              </Tooltip>
            )}
            {status === 'checked_in' && (
              <Tooltip title="Checked in" arrow>
                <LoginIcon sx={{ fontSize: 14 }} />
              </Tooltip>
            )}
            {appointment.invoiceId && (
              <Tooltip title="Billing complete" arrow>
                <ReceiptIcon sx={{ fontSize: 14 }} />
              </Tooltip>
            )}
            {alerts.length > 0 && (
              <Tooltip
                arrow
                title={
                  <Box>
                    {alerts.map((alert: any, idx: number) => (
                      <Typography key={idx} variant="caption" display="block">
                        {alert?.text || alert}
                      </Typography>
                    ))}
                  </Box>
                }
              >
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', color: alerts.some((a: any) => a?.isCritical) ? 'error.main' : 'warning.dark' }}
                >
                  <WarningIcon sx={{ fontSize: 14 }} />
                  <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, ml: 0.25 }}>{alerts.length}</Box>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
        {shown.has('subject') && (
          <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.25, pointerEvents: 'none' }}>
            {subject}
          </Typography>
        )}
        {shown.has('type') && (
          <Typography noWrap sx={{ fontSize: '0.75rem', lineHeight: 1.25, pointerEvents: 'none' }}>
            {appointment.type}
          </Typography>
        )}
        {shown.has('owner') && (
          <Box aria-label={`Owner ${owner}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0, fontSize: '0.72rem', lineHeight: 1.3, opacity: 0.85, pointerEvents: 'none' }}>
            <OwnerIcon sx={{ fontSize: 13, flex: 'none' }} />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{owner}</Box>
          </Box>
        )}
        {shown.has('place') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0, fontSize: '0.72rem', lineHeight: 1.3, pointerEvents: 'none' }}>
            <PlaceIcon sx={{ fontSize: 13, flex: 'none' }} />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{barnName}</Box>
          </Box>
        )}
        {shown.has('equipment') && (
          <Typography noWrap sx={{ fontSize: '0.7rem', lineHeight: 1.3, fontStyle: 'italic', opacity: 0.9, pointerEvents: 'none' }}>
            {equipmentNames}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

/**
 * One block for a barn visit's horses in a column (they share one window by design), or for a barn
 * booking with no horses, so every barn booking looks alike. A click opens the appointment window
 * (the first horse, with the rest of the visit listed; it links to the visit page for charging), a
 * horse row opens that horse, and dragging moves the whole visit (handleDragEnd, `visit:` ids).
 */
const DraggableVisitGroup: FC<{
  dragId: string;
  members: Appointment[];
  position: { exactTop: number; height: number };
  onOpenDetails: (appointment: Appointment) => void;
  typeColorMap?: Record<string, string>;
  canReschedule: boolean;
  barn?: Barn | null;
}> = ({ dragId, members, position, onOpenDetails, typeColorMap, canReschedule, barn }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    disabled: !canReschedule,
  });
  const ordered = [...members].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const first = ordered[0];
  const noHorses = !first.visitGroupId;
  const startMs = new Date(first.date).getTime();
  const endMs = Math.max(...members.map((m) => new Date(m.date).getTime() + (m.duration || 60) * 60_000));
  const startISO = new Date(startMs).toISOString();
  const minutes = Math.round((endMs - startMs) / 60_000);
  const title = first.barn?.name || barn?.name || first.location;
  const height = position.height - 4;
  // One row per horse when they fit, else their names on one line.
  const rowsFit = Math.floor((height - 52) / 19) >= members.length;
  // Horses seen one after another show their own start; a visit booked as one window does not.
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const staggered = new Set(members.map((m) => m.date)).size > 1;
  const tentative = members.every((m) => normalizeStatus(m.status) === 'tentative');
  const soloStatus = members.length === 1 ? normalizeStatus(first.status) : null;
  const startOf = (m: Appointment) => DateTime.fromISO(m.date, { zone: 'utc' }).setZone(zone).toFormat('h:mm');
  const horses = ordered.map((m) => ({
    name: m.horse?.name ?? getAppointmentSubject(m).name,
    type: m.type,
    alert: (m.horse?.medicalAlerts ?? []).map((a: any) => a?.text || a).filter(Boolean).join('; '),
    time: staggered ? DateTime.fromISO(m.date, { zone: 'utc' }).setZone(zone).toFormat('h:mm a') : undefined,
  }));

  return (
    <Tooltip
      {...hoverTooltipProps}
      title={
        isDragging ? '' : (
          <AppointmentHoverCard
            appointment={{ ...first, horse: null, client: null, date: startISO, duration: minutes, notes: first.notes }}
            barn={barn}
            visitHorses={noHorses ? undefined : horses}
          />
        )
      }
    >
      <Box
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Barn visit at ${title}, ${noHorses ? `no horses, ${first.type}` : `${members.length} horse${members.length === 1 ? '' : 's'}`}`}
        onClick={(e) => {
          if (!isDragging) {
            e.stopPropagation();
            onOpenDetails(first);
          }
        }}
        sx={{
          position: 'absolute',
          top: `${position.exactTop + 2}px`,
          left: '4px',
          right: '4px',
          height: `${height}px`,
          minHeight: '20px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          color: '#3B2E1A',
          border: `1px ${tentative ? 'dashed' : 'solid'} #CDB9A0`,
          opacity: soloStatus === 'no_show' ? 0.55 : 1,
          borderRadius: 2,
          cursor: canReschedule ? 'grab' : 'pointer',
          zIndex: isDragging ? 1000 : 10,
          boxShadow: isDragging ? '0 8px 20px rgba(0,0,0,0.18)' : 'none',
          '&:hover': { boxShadow: '0 0 0 2px #CDB9A0' },
          transition: isDragging ? 'none' : 'box-shadow 0.15s',
          ...(transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.6 : 1 } : {}),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5, bgcolor: '#F6EFE6', borderBottom: '1px solid #E6D8C6' }}>
          <WarehouseIcon sx={{ fontSize: 15, flex: 'none' }} />
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', fontWeight: 600, pointerEvents: 'none' }}>{title}</Typography>
          <Box component="span" sx={{ flex: 'none', px: 0.75, borderRadius: 999, bgcolor: 'background.paper', fontSize: '0.7rem', fontWeight: 600 }}>
            {noHorses ? 'No horses' : `${members.length} horse${members.length === 1 ? '' : 's'}`}
          </Box>
          {soloStatus === 'completed' && <CheckCircleIcon titleAccess="Completed" sx={{ fontSize: 14, flex: 'none' }} />}
          {soloStatus === 'checked_in' && <LoginIcon titleAccess="Checked in" sx={{ fontSize: 14, flex: 'none' }} />}
          {members.length === 1 && first.invoiceId && <ReceiptIcon titleAccess="Billing complete" sx={{ fontSize: 14, flex: 'none' }} />}
        </Box>
        <Box sx={{ px: 1, py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography noWrap sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#6B5A43', pointerEvents: 'none' }}>
            {blockTimeRange(startISO, minutes)} · {noHorses ? first.type : 'Barn visit'}
          </Typography>
          {!noHorses && !rowsFit && (
            <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 600, pointerEvents: 'none' }}>
              {ordered.map((m) => m.horse?.name ?? getAppointmentSubject(m).name).join(', ')}
            </Typography>
          )}
          {!noHorses && rowsFit && ordered.map((m) => (
            <Box
              key={m.id}
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetails(m);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              // dnd-kit's keyboard sensor starts a drag from any focused descendant; Enter here opens.
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Open ${m.horse?.name ?? getAppointmentSubject(m).name}'s appointment`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                font: 'inherit',
                color: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                '&:hover .visit-horse': { textDecoration: 'underline' },
              }}
            >
              {staggered && (
                <Box component="span" sx={{ flex: 'none', width: 30, fontSize: '0.72rem', color: '#6B5A43' }}>
                  {startOf(m)}
                </Box>
              )}
              <Box component="span" sx={{ flex: 'none', width: 8, height: 8, borderRadius: '50%', bgcolor: getAppointmentColor(m, typeColorMap) }} />
              <Box component="span" className="visit-horse" sx={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {m.horse?.name ?? getAppointmentSubject(m).name}
              </Box>
              <Box component="span" sx={{ fontSize: '0.74rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.type}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Tooltip>
  );
};

const Appointments: FC = () => {
  const { trackFeatureUsed } = useAnalytics();
  const rbac = useRBAC();
  const { claims } = rbac;
  /**
   * ⚠ THIS ROLE CHECK STAYS, and it is the only kind that may: the SERVER gates closures on a
   * ROLE too. `TimeBlocksService.assertClosureRole` throws for anything but OWNER/ADMIN when
   * `vetId` is null (ADR-0028), and no decorator can express a payload-dependent rule, so
   * `role-guard-coverage.spec.ts` exempts those handlers in writing. Mirroring a role gate with a
   * role check is correct; mirroring a PERMISSION gate with one is the bug this task removes.
   */
  const canManageClosures = claims?.role === 'OWNER' || claims?.role === 'ADMIN';
  /**
   * ⚠ AND TIME BLOCKS THEMSELVES ARE UNGATED, which is why `can` covers appointments only.
   * `time-blocks.controller.ts` declares NO permission on create/update/remove — any member books
   * a provider block (front desk and techs do) — and the coverage spec's exemption says so
   * explicitly: "manage:appointments is held by every role, CSR included, so a decorator denies
   * nobody". Adding a slug here would be the web inventing a rule the API does not have.
   */
  const can = scheduleControls(rbac);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600px - 900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // < 900px


  // View state
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewType, setViewType] = useState<ViewType>('daily');
  const [timeInterval, setTimeInterval] = useState<TimeInterval>(60);
  // One person's calendar settings (server-saved, so they follow them between computers).
  const { prefs: schedulePrefs, save: saveSchedulePrefs } = useSchedulePreferences();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const appliedSlotPref = useRef(false);
  useEffect(() => {
    if (!appliedSlotPref.current && schedulePrefs.slotMinutes) {
      appliedSlotPref.current = true;
      setTimeInterval(schedulePrefs.slotMinutes);
    }
  }, [schedulePrefs.slotMinutes]);
  const changeSlotSize = (minutes: TimeInterval) => {
    setTimeInterval(minutes);
    saveSchedulePrefs({ slotMinutes: minutes });
  };
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Day view columns: one per provider (default) or one per piece of equipment.
  const [columnMode, setColumnMode] = useState<'providers' | 'equipment'>('providers');
  const [preSelectedEquipmentId, setPreSelectedEquipmentId] = useState('');
  const [providerMenuAnchor, setProviderMenuAnchor] = useState<null | HTMLElement>(null);
  const [blockMenuAnchor, setBlockMenuAnchor] = useState<null | HTMLElement>(null);
  const [calendarPopoverAnchor, setCalendarPopoverAnchor] = useState<null | HTMLElement>(null);
  
  // Existing appointment dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedDialogDate, setSelectedDialogDate] = useState<Date | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(60);
  const [selectedVetForDialog, setSelectedVetForDialog] = useState<string>('');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Equipment schedule confirmation dialog state
  const [equipConfirmOpen, setEquipConfirmOpen] = useState(false);
  const [equipConfirmWindows, setEquipConfirmWindows] = useState<
    Array<{ itemId: string; name: string; startTime: Date; endTime: Date }>
  >([]);
  const [pendingUpdate, setPendingUpdate] = useState<{
    appointmentId: string;
    data: any;
  } | null>(null);
  const [editFormData, setEditFormData] = useState<EditForm>({
    date: '',
    duration: 60,
    type: '',
    location: '',
    notes: '',
    assignedVetId: '',
    status: 'scheduled',
    barnId: '',
  });
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [editEquipmentIds, setEditEquipmentIds] = useState<string[]>([]);
  // True only when the user actually touched equipment in the edit form. The
  // save payload includes `equipment` ONLY when dirty: a replica-hydrated
  // appointment row carries no equipmentSchedules, so an unconditional
  // equipment list would read as [] and the backend would interpret the save
  // as "wipe all reservations".
  const [editEquipmentDirty, setEditEquipmentDirty] = useState(false);
  const [editEquipmentWindows, setEditEquipmentWindows] = useState<
    Record<string, { startTime: Date; endTime: Date }>
  >({});
  const [editEquipmentAllDay, setEditEquipmentAllDay] = useState<Record<string, boolean>>({});
  const isOffline = useIsOffline();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Time block state
  const [isTimeBlockDialogOpen, setIsTimeBlockDialogOpen] = useState(false);
  const [timeBlockVet, setTimeBlockVet] = useState<Veterinarian | null>(null);
  const [editingTimeBlock, setEditingTimeBlock] = useState<any>(null);
  const [selectedTimeBlock, setSelectedTimeBlock] = useState<any>(null);
  const [isDeleteTimeBlockOpen, setIsDeleteTimeBlockOpen] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  // Clinic-zone renderer for the INSTANT plane (Appointment.date). Only the
  // detail dialog's "Date & Time" string goes through it today; the calendar
  // grid's own day-membership and slot math is luxon in the BROWSER'S zone
  // (`userTimeZone` below) and is a separate, larger change.
  const formatInstant = useFormatInstant();

  const handleGenerateInvoice = () => {
    if (selectedAppointment) {
      // Navigate to billing page with appointment pre-selected
      navigate('/billing', { state: { appointmentId: selectedAppointment.id, autoImport: true } });
      setSelectedAppointment(null);
    }
  };

  // Fetch appointments scoped to the VISIBLE date range. Previously this
  // page fetched every appointment in the clinic and filtered client-side,
  // which broke on clinics with thousands of historical appointments.
  const isToday = format(currentDate, 'yyyy-MM-dd') === todayLocalISO();
  const isMonday = format(currentDate, 'EEEE') === 'Monday';
  const needsFreshData = isToday || (isMonday && viewType === 'weekly');

  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);
  // The month grid shows six full weeks, so it fetches the neighbour days it draws as well.
  const monthDays = useMemo(() => getMonthGridDays(currentDate), [currentDate]);

  const { dateFrom, dateTo } = useMemo(() => {
    if (viewType === 'weekly') {
      return {
        dateFrom: startOfDay(weekDays[0]).toISOString(),
        dateTo: endOfDay(weekDays[6]).toISOString(),
      };
    }
    if (viewType === 'monthly') {
      return {
        dateFrom: startOfDay(monthDays[0]).toISOString(),
        dateTo: endOfDay(monthDays[monthDays.length - 1]).toISOString(),
      };
    }
    // Daily
    return {
      dateFrom: startOfDay(currentDate).toISOString(),
      dateTo: endOfDay(currentDate).toISOString(),
    };
  }, [currentDate, viewType, weekDays, monthDays]);

  const { data: appointments = [], isLoading } = useQuery<Appointment[]>(
    ['appointments', { dateFrom, dateTo, viewType }],
    withOfflineFallback(
      () => fetchAppointments({ dateFrom, dateTo }) as Promise<Appointment[]>,
      () => readAppointmentsInRange({ dateFrom, dateTo }).then((res) => res.data) as Promise<Appointment[]>,
    ),
    {
      // Always keep the last range on screen while the next one loads. Dropping it (as this did
      // whenever today was in view) blanked the page to a spinner on every step back to today.
      keepPreviousData: true,
      refetchOnWindowFocus: needsFreshData,
      staleTime: needsFreshData ? 0 : 5 * 60 * 1000,
    }
  );

  // A link from Reports' cancelled and no-show list opens one appointment: its day first, then its
  // window once that day's rows arrive. The link's state is read once, keyed on location.key.
  const location = useLocation();
  const linkedRef = useRef<{ key: string; id: string | null }>({ key: '', id: null });
  useEffect(() => {
    const link = (location.state as { openAppointment?: { id: string; date: string } } | null)?.openAppointment;
    if (!link || linkedRef.current.key === location.key) return;
    linkedRef.current = { key: location.key, id: link.id };
    setViewType('daily');
    setCurrentDate(new Date(link.date));
  }, [location]);
  useEffect(() => {
    const apt = linkedRef.current.id ? appointments.find((a) => a.id === linkedRef.current.id) : undefined;
    if (!apt) return;
    linkedRef.current = { ...linkedRef.current, id: null };
    setSelectedAppointment(apt);
  }, [appointments]);

  const { data: veterinarians = [], isLoading: isLoadingVets } = useQuery<Veterinarian[]>(
    'veterinarians',
    withOfflineFallback(fetchVeterinarians, () => readVeterinarians() as Promise<Veterinarian[]>),
  );

  const { data: appointmentCatalogItems = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', 'appointment'],
    () => serviceCatalogService.getAll({ category: 'appointment' })
  );

  const { data: timeBlocks = [] } = useQuery<any[]>(
    ['time-blocks'],
    withOfflineFallback(
      appointmentService.listTimeBlocks,
      // Online fetches ALL blocks (range-less key), so the replica fallback does
      // too; the calendar filters to the visible range itself. Without this,
      // offline-created blocks (queued via executeWrite) were invisible.
      () => readTimeBlocks(),
    )
  );

  // Equipment items for the edit form — fetched when edit mode opens
  const editApptStart = editFormData.date ? new Date(editFormData.date) : null;
  const editApptEnd = editApptStart ? new Date(editApptStart.getTime() + editFormData.duration * 60_000) : null;
  const { data: editEquipmentItems = [] } = useQuery<Array<{ id: string; name: string; type: string; available: boolean }>>(
    ['equipment-available', editApptStart?.toISOString(), editApptEnd?.toISOString(), selectedAppointment?.id, editFormData.assignedVetId],
    async () => {
      const params = new URLSearchParams();
      if (editApptStart) params.set('start', editApptStart.toISOString());
      if (editApptEnd) params.set('end', editApptEnd.toISOString());
      if (selectedAppointment?.id) params.set('excludeAppointmentId', selectedAppointment.id);
      // A unit held by THIS vet stays selectable (reuse); only other vets' overlaps block it.
      if (editFormData.assignedVetId) params.set('assignedVetId', editFormData.assignedVetId);
      const response = await api.get(`/equipment/available?${params}`);
      return response.data;
    },
    { enabled: isEditMode && !!editApptStart && !!editApptEnd && skipWhenOffline() }
  );

  const deleteTimeBlockMutation = useMutation(
    // Takes a LIST so "delete all days of a range" is one action. Closures
    // delete via direct DELETE (online-only — the sync push rejects queued
    // closure deletes, ADR-0028); vet blocks ride the offline op-queue.
    async (blocks: any[]) => {
      for (const block of blocks) {
        if (block.vetId === null) {
          await api.delete(`/time-blocks/${block.id}`).then((r) => r.data);
        } else {
          await executeDelete({
            entityType: 'timeBlock',
            table: 'timeBlocks',
            entityId: block.id,
            networkFn: () => api.delete(`/time-blocks/${block.id}`).then((r) => r.data),
          });
        }
      }
      return blocks.length;
    },
    {
      onSuccess: (count: number) => {
        queryClient.invalidateQueries(['time-blocks']);
        enqueueSnackbar(count > 1 ? `Deleted ${count} blocked days` : 'Time block deleted', { variant: 'success' });
        setSelectedTimeBlock(null);
        setIsDeleteTimeBlockOpen(false);
      },
      onError: (err: any) => {
        enqueueSnackbar(err.response?.data?.message || 'Failed to delete time block', { variant: 'error' });
      },
    }
  );

  const appointmentTypes = useMemo(() => {
    const items = appointmentCatalogItems.filter(item => item.isActive);
    if (items.length > 0) return items.map(item => item.name);
    return ['Performance Evaluation', 'PPE (Pre-Purchase Exam)', 'Vaccination', 'Dental Care', 'Emergency', 'Surgery'];
  }, [appointmentCatalogItems]);

  // Lowercased type/alias -> clinic-configured calendar color (Settings → Service Pricing)
  const typeColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    // Names first, then aliases that name no other type (the appointments report's rule).
    for (const item of appointmentCatalogItems) if (item.color) map[item.name.toLowerCase()] = item.color;
    for (const item of appointmentCatalogItems) {
      if (!item.color || !Array.isArray(item.aliases)) continue;
      for (const alias of item.aliases) if (!(alias.toLowerCase() in map)) map[alias.toLowerCase()] = item.color;
    }
    return map;
  }, [appointmentCatalogItems]);

  // Check if selected appointment already has an invoice
  const hasInvoice = useMemo(() => {
    if (!selectedAppointment) return false;
    // Check if appointment has invoiceId directly (new schema)
    return !!selectedAppointment.invoiceId;
  }, [selectedAppointment]);

  // Who an appointment can go to: the vets and owners, plus anyone already holding work in range.
  const panelProviders = useMemo(() => {
    const list = veterinarians.map((v) => ({ id: v.id, name: v.name }));
    for (const apt of appointments) {
      if (apt.assignedVetId && apt.assignedVet && !list.some((p) => p.id === apt.assignedVetId)) {
        list.push({ id: apt.assignedVetId, name: apt.assignedVet.name });
      }
    }
    return list;
  }, [veterinarians, appointments]);
  const typeOptions = useMemo(
    () =>
      appointmentTypes.map((name) => ({
        id: appointmentCatalogItems.find((item) => item.name === name)?.id,
        name,
        color: getAppointmentColor({ type: name } as Appointment, typeColorMap),
      })),
    [appointmentTypes, typeColorMap, appointmentCatalogItems],
  );

  // Hours shown: the person's setting (default 6 AM to midnight), stretched so no appointment in
  // range falls outside the grid.
  const { gridStartHour, gridEndHour } = useMemo(() => {
    let startH = schedulePrefs.dayStartHour ?? 6;
    let endH = (schedulePrefs.dayEndHour ?? 24) - 1;
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    for (const apt of appointments) {
      if (isCancelled(apt.status)) continue;
      const s = DateTime.fromISO(apt.date, { zone: 'utc' }).setZone(zone);
      const e = s.plus({ minutes: apt.duration || 60 });
      startH = Math.min(startH, s.hour);
      endH = Math.max(endH, s.hour, e.hasSame(s, 'day') ? (e.minute > 0 ? e.hour : e.hour - 1) : 23);
    }
    return { gridStartHour: startH, gridEndHour: Math.min(endH, 23) };
  }, [appointments, schedulePrefs.dayStartHour, schedulePrefs.dayEndHour]);

  // Generate time slots based on current interval
  const timeSlots = useMemo(
    () => generateTimeSlots(timeInterval, gridStartHour, gridEndHour),
    [timeInterval, gridStartHour, gridEndHour],
  );

  // Filter providers based on selection and add unassigned column
  const filteredProviders = useMemo(() => {
    // If a specific provider is selected, show only that provider
    if (selectedProviderId === 'unassigned') {
      return [{ id: 'unassigned', name: 'Unassigned', professionalTitle: 'No Vet Assigned' }];
    } else if (selectedProviderId) {
      return veterinarians.filter(vet => vet.id === selectedProviderId);
    }
    
    // Show all providers plus unassigned column
    return [
      { id: 'unassigned', name: 'Unassigned', professionalTitle: 'No Vet Assigned' },
      ...veterinarians
    ];
  }, [veterinarians, selectedProviderId]);

  // Filter appointments for the current date/week/month and organize by provider. A cancelled
  // appointment stays on record (and in its panel's visit list) but holds no slot on the grid.
  const rangeAppointments = useMemo(() => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const live = appointments.filter((apt) => !isCancelled(apt.status));

    if (viewType === 'daily') {
      const currentDateStr = format(currentDate, 'yyyy-MM-dd');
      
      return live.filter(apt => {
        // Convert appointment date from UTC to user's timezone for comparison
        const aptDate = DateTime.fromISO(apt.date, { zone: 'utc' })
          .setZone(userTimeZone)
          .toFormat('yyyy-MM-dd');
        return aptDate === currentDateStr;
      });
    }
    // Week, or the month grid's six weeks (neighbour days included)
    const days = viewType === 'weekly' ? weekDays : monthDays;
    const firstStr = format(days[0], 'yyyy-MM-dd');
    const lastStr = format(days[days.length - 1], 'yyyy-MM-dd');
    return live.filter(apt => {
      const aptDateStr = DateTime.fromISO(apt.date, { zone: 'utc' })
        .setZone(userTimeZone)
        .toFormat('yyyy-MM-dd');
      return aptDateStr >= firstStr && aptDateStr <= lastStr;
    });
  }, [appointments, currentDate, viewType, weekDays, monthDays]);

  // The search box narrows every view to appointments whose horse, owner, barn, vet or type matches.
  const searchTerm = search.trim().toLowerCase();
  const filteredAppointments = useMemo(() => {
    if (!searchTerm) return rangeAppointments;
    const matches = (apt: Appointment) =>
      [
        apt.horse?.name,
        apt.client?.name,
        apt.barn?.name,
        apt.location,
        apt.type,
        apt.assignedVet?.name,
        ...(Array.isArray(apt.horse?.owners) ? apt.horse!.owners!.map((o) => o.name) : []),
      ].some((value) => value?.toLowerCase().includes(searchTerm));
    // A barn visit stays whole, so its block lists every horse a drag will move.
    const visits = new Set(rangeAppointments.filter(matches).map((apt) => apt.visitGroupId).filter(Boolean));
    return rangeAppointments.filter((apt) => matches(apt) || (!!apt.visitGroupId && visits.has(apt.visitGroupId)));
  }, [rangeAppointments, searchTerm]);

  // Barn addresses and contacts for the hover card. Same cache as the booking dialog's barn
  // picker; barns are not in the offline replica, so offline the card shows the barn name only.
  const { data: barns = [] } = useQuery<Barn[]>(['barns'], () => barnsService.list(), {
    // GET /barns asks the Barns screen's read slugs; without them the card shows the name only.
    enabled: !isOffline && canViewScreen(SCREEN_ACCESS.barns, rbac.hasPermission),
    staleTime: 60_000,
  });
  const barnById = useMemo(() => new Map(barns.map((b) => [b.id, b])), [barns]);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const appointmentsByDay = useMemo(() => {
    const byDay: Record<string, Appointment[]> = {};
    for (const apt of filteredAppointments) {
      const key = DateTime.fromISO(apt.date, { zone: 'utc' }).setZone(userTimeZone).toFormat('yyyy-MM-dd');
      (byDay[key] = byDay[key] ?? []).push(apt);
    }
    return byDay;
  }, [filteredAppointments, userTimeZone]);
  const dayProviderCounts = useMemo(
    () => countByDayAndProvider(filteredAppointments, userTimeZone),
    [filteredAppointments, userTimeZone],
  );

  const providerColor = useMemo(() => {
    const colors: Record<string, { bg: string; fg: string }> = {};
    veterinarians.forEach((vet, i) => { colors[vet.id] = PROVIDER_COLORS[i % PROVIDER_COLORS.length]; });
    return colors;
  }, [veterinarians]);

  // Time-grid columns. Day: one per provider. Week: one per day, split into a column per vet
  // when no single provider is picked; Unassigned joins only in weeks that have unassigned visits.
  const isWeekSplit = viewType === 'weekly' && !selectedProviderId;
  const gridColumns = useMemo(() => {
    // Unassigned: the person's setting (default: only when something in view is unassigned).
    const unassignedPref = schedulePrefs.unassignedColumn ?? 'auto';
    const showUnassigned =
      unassignedPref === 'always' || (unassignedPref === 'auto' && filteredAppointments.some((a) => !a.assignedVetId));
    // Anyone holding work in view gets a column, vet or not (columns follow the work).
    const extras: Veterinarian[] = [];
    for (const apt of filteredAppointments) {
      if (apt.assignedVetId && apt.assignedVet && !veterinarians.some((v) => v.id === apt.assignedVetId) && !extras.some((v) => v.id === apt.assignedVetId)) {
        extras.push({ ...apt.assignedVet, id: apt.assignedVetId });
      }
    }
    const everyone = [...(showUnassigned ? [UNASSIGNED_PROVIDER] : []), ...veterinarians, ...extras];
    if (viewType === 'daily') {
      const columns = selectedProviderId ? filteredProviders : everyone.length > 0 ? everyone : [UNASSIGNED_PROVIDER];
      return columns.map((provider, i) => ({ key: provider.id, provider, date: currentDate, dayStart: i === 0 }));
    }
    const providers: Veterinarian[] = isWeekSplit ? everyone : filteredProviders;
    return weekDays.flatMap((day) =>
      providers.map((provider, i) => ({ key: `${format(day, 'yyyy-MM-dd')}-${provider.id}`, provider, date: day, dayStart: i === 0 })),
    );
  }, [viewType, filteredProviders, currentDate, isWeekSplit, filteredAppointments, veterinarians, weekDays, schedulePrefs.unassignedColumn, selectedProviderId]);
  const providersPerDay = viewType === 'weekly' ? gridColumns.length / 7 : 1;
  const monthProviders: Veterinarian[] = selectedProviderId
    ? filteredProviders
    : [...veterinarians, ...filteredProviders.filter((p) => p.id === 'unassigned')];

  // Toolbar text and the counts row.
  const viewUnit = viewType === 'daily' ? 'day' : viewType === 'weekly' ? 'week' : 'month';
  const dateLabel =
    viewType === 'daily'
      ? format(currentDate, 'EEEE, MMMM d')
      : viewType === 'weekly'
        ? `${format(weekDays[0], 'MMM d')} - ${format(weekDays[6], weekDays[0].getMonth() === weekDays[6].getMonth() ? 'd, yyyy' : 'MMM d, yyyy')}`
        : format(currentDate, 'MMMM yyyy');
  // The grid places and labels times in THIS computer's zone (clinic-zone grid is a separate change).
  const timeZoneLabel = useMemo(() => {
    const name = new Intl.DateTimeFormat('en-US', { timeZoneName: 'long' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return `Times shown in ${name ?? userTimeZone}`;
  }, [userTimeZone]);
  const providerFilterLabel =
    selectedProviderId === null
      ? 'All providers'
      : selectedProviderId === 'unassigned'
        ? 'Unassigned'
        : veterinarians.find((v) => v.id === selectedProviderId)?.name ?? 'Provider';
  const providerAvatars: Veterinarian[] = selectedProviderId ? filteredProviders.slice(0, 1) : veterinarians.slice(0, 3);
  const rangeCounts = useMemo(() => {
    const shown = filteredAppointments.filter(
      (apt) =>
        !selectedProviderId ||
        (selectedProviderId === 'unassigned' ? !apt.assignedVetId : apt.assignedVetId === selectedProviderId),
    );
    const barnVisits = new Set(shown.filter((apt) => apt.barnId || apt.barn).map((apt) => apt.visitGroupId ?? apt.id));
    return {
      total: shown.length,
      barnVisits: barnVisits.size,
      unassigned: filteredAppointments.filter((apt) => !apt.assignedVetId).length,
    };
  }, [filteredAppointments, selectedProviderId]);
  // By equipment (Day, Week and Month): each unit's bookings on every day in view.
  const showEquipment = columnMode === 'equipment';
  const { data: equipmentUnits = [], isLoading: isLoadingEquipment } = useQuery(
    ['equipment', 'active'],
    appointmentService.listEquipment,
    { enabled: showEquipment && !isOffline, staleTime: 60_000 },
  );
  // The range's live appointments before search: a unit booked by a row the search hides is still busy.
  const equipmentByDay = useMemo(() => {
    const byDay: Record<string, Record<string, EquipmentReservation[]>> = {};
    if (!showEquipment) return byDay;
    const days = viewType === 'daily' ? [currentDate] : viewType === 'weekly' ? weekDays : monthDays;
    for (const day of days) {
      byDay[format(day, 'yyyy-MM-dd')] = buildEquipmentReservations(equipmentUnits, rangeAppointments, day, barnById);
    }
    return byDay;
  }, [showEquipment, viewType, currentDate, weekDays, monthDays, equipmentUnits, rangeAppointments, barnById]);
  const equipmentReservations = equipmentByDay[format(currentDate, 'yyyy-MM-dd')] ?? {};
  const equipmentCounts = useMemo(() => {
    const all = Object.values(equipmentByDay).flatMap((byUnit) => Object.values(byUnit).flat());
    return { bookings: all.length, offsite: all.filter((res) => res.where).length, unassigned: all.some((res) => !res.vetId) };
  }, [equipmentByDay]);
  const legendTypes = useMemo(() => {
    const seen = new Map<string, { label: string; fill: string; line: string }>();
    filteredAppointments.forEach((apt) => {
      const key = apt.type.toLowerCase();
      if (!seen.has(key)) {
        const palette = blockPalette(getAppointmentColor(apt, typeColorMap));
        seen.set(key, { label: apt.type, fill: palette.fill, line: palette.line });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredAppointments, typeColorMap]);

  const formatAppointmentTime = (appointment: Appointment): string => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const start = DateTime.fromISO(appointment.date, { zone: 'utc' }).setZone(userTimeZone);
    const durationMinutes = appointment.duration || 60;
    const end = start.plus({ minutes: durationMinutes });
    
    return `${start.toFormat('h:mm a')} - ${end.toFormat('h:mm a')}`;
  };

  const getAppointmentPosition = (appointment: Appointment) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const start = DateTime.fromISO(appointment.date, { zone: 'utc' }).setZone(userTimeZone);
    const startHour = start.hour;
    const startMinute = start.minute;
    
    // Find the base slot (hour) for this appointment
    let baseSlotIndex = timeSlots.findIndex(slot => 
      slot.hour === startHour && slot.isHourMark
    );
    
    // If no hour mark found, find the closest slot
    if (baseSlotIndex === -1) {
      baseSlotIndex = timeSlots.findIndex(slot => slot.hour >= startHour) || 0;
    }
    
    const durationMinutes = appointment.duration || 60;
    const slotHeight = timeInterval === 15 ? 40 : timeInterval === 30 ? 50 : 60;
    
    // Calculate exact position within the time grid
    let exactTop = 0;
    let height = 0;
    
    if (timeInterval === 60) {
      // For 1-hour intervals, calculate precise positioning within the hour
      const minutesFromHourStart = startMinute;
      const hourSlotTop = baseSlotIndex * slotHeight;
      const pixelsPerMinute = slotHeight / 60; // 60 minutes per hour slot
      
      exactTop = hourSlotTop + (minutesFromHourStart * pixelsPerMinute);
      height = (durationMinutes * pixelsPerMinute);
    } else {
      // For smaller intervals, use slot-based positioning
      let exactSlotIndex = timeSlots.findIndex(slot => 
        slot.hour === startHour && slot.minute >= startMinute
      );
      
      if (exactSlotIndex === -1) {
        exactSlotIndex = baseSlotIndex;
      }
      
      exactTop = exactSlotIndex * slotHeight;
      const slotsSpanned = Math.ceil(durationMinutes / timeInterval);
      height = slotsSpanned * slotHeight;
    }
    
    return { exactTop, height, slotIndex: baseSlotIndex };
  };

  const getTimeBlockPosition = (block: any, dateOverride?: Date) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const start = DateTime.fromISO(block.startTime, { zone: 'utc' }).setZone(userTimeZone);
    const end = DateTime.fromISO(block.endTime, { zone: 'utc' }).setZone(userTimeZone);

    const slotHeight = timeInterval === 15 ? 40 : timeInterval === 30 ? 50 : 60;

    const clampedStartHour = Math.max(start.hour + start.minute / 60, gridStartHour);
    const clampedEndHour = Math.min(end.hour + end.minute / 60, gridEndHour + 1);

    if (clampedStartHour >= clampedEndHour) return null;

    const pixelsPerMinute = slotHeight / timeInterval;
    const minutesFromGridStart = (clampedStartHour - gridStartHour) * 60;
    const durationMinutes = (clampedEndHour - clampedStartHour) * 60;

    return {
      exactTop: minutesFromGridStart * pixelsPerMinute,
      height: durationMinutes * pixelsPerMinute,
    };
  };

  const getTimeBlocksForProvider = (providerId: string, dateStr?: string) => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const targetDate = dateStr || format(currentDate, 'yyyy-MM-dd');

    return timeBlocks.filter((block: any) => {
      // Clinic-wide closures (vetId null, ADR-0028) render in EVERY column —
      // including Unassigned, since they block unassigned bookings too. A
      // vet's personal block stays in that vet's column only.
      const matchesProvider =
        block.vetId === null ||
        (providerId !== 'unassigned' && block.vetId === providerId);
      if (!matchesProvider) return false;
      const blockDate = DateTime.fromISO(block.startTime, { zone: 'utc' })
        .setZone(userTimeZone)
        .toFormat('yyyy-MM-dd');
      return blockDate === targetDate;
    });
  };

  // Time-grid geometry, shared by the header rows and the body so they stay on one set of tracks.
  const slotHeight = timeInterval === 15 ? 40 : timeInterval === 30 ? 50 : 60;
  const timeColWidth = isMobile ? 64 : 84;
  const minColWidth = viewType === 'daily' ? (isMobile ? 120 : 160) : isWeekSplit ? (isMobile ? 60 : 68) : isMobile ? 100 : 120;
  const HEADER_ROW_HEIGHT = 56;
  const SUBHEADER_ROW_HEIGHT = 34;
  const GRID_START_MINUTES = gridStartHour * 60; // where timeSlots starts
  const minutesToPx = (minutes: number) =>
    timeInterval === 60 ? minutes * (slotHeight / 60) : Math.ceil(minutes / timeInterval) * slotHeight;

  // The current-time line moves once a minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowTop =
    nowMinutes >= GRID_START_MINUTES && nowMinutes < (gridEndHour + 1) * 60 ? (nowMinutes - GRID_START_MINUTES) * (slotHeight / timeInterval) : null;
  const todayKey = todayLocalISO();
  const showsToday =
    viewType === 'daily'
      ? format(currentDate, 'yyyy-MM-dd') === todayKey
      : viewType === 'weekly' && weekDays.some((day) => format(day, 'yyyy-MM-dd') === todayKey);

  // Open the grid at the working day instead of 6 AM: an hour and a half before now on today,
  // else 7 AM. Re-runs when the view, the dates shown or the slot size change.
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const gridReady = !isLoading && !isLoadingVets;
  const scrollKey = `${viewType}|${format(viewType === 'weekly' ? weekDays[0] : currentDate, 'yyyy-MM-dd')}|${timeInterval}|${gridReady}|${gridStartHour}|${showEquipment && equipmentUnits.length > 0}`;
  useEffect(() => {
    const el = gridScrollRef.current;
    if (!el) return;
    if (viewType === 'monthly' || (showEquipment && viewType === 'weekly')) {
      el.scrollTop = 0;
      return;
    }
    // A person who set their hours starts the day at the top of them.
    const workStart = schedulePrefs.dayStartHour != null ? GRID_START_MINUTES : Math.max(GRID_START_MINUTES, 7 * 60);
    const target = showsToday ? Math.max(GRID_START_MINUTES, nowMinutes - 90) : workStart;
    el.scrollTop = (target - GRID_START_MINUTES) * (slotHeight / timeInterval);
  }, [scrollKey]);

  const renderTimeBlock = (block: any) => {
    const position = getTimeBlockPosition(block);
    if (!position) return null;
    const startStr = DateTime.fromISO(block.startTime, { zone: 'utc' }).setZone(userTimeZone).toFormat('h:mm a');
    const endStr = DateTime.fromISO(block.endTime, { zone: 'utc' }).setZone(userTimeZone).toFormat('h:mm a');
    return (
      <Box
        key={`block-${block.id}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedTimeBlock(block);
        }}
        sx={{
          position: 'absolute',
          top: `${position.exactTop}px`,
          left: '2px',
          right: '2px',
          height: `${position.height}px`,
          minHeight: '20px',
          backgroundColor: 'rgba(211, 47, 47, 0.08)',
          borderLeft: '3px solid #d32f2f',
          borderRadius: '0 4px 4px 0',
          p: 0.5,
          overflow: 'hidden',
          cursor: 'pointer',
          zIndex: 1,
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.15)' },
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(211,47,47,0.04) 5px, rgba(211,47,47,0.04) 10px)',
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#d32f2f', fontSize: '0.65rem', lineHeight: 1.2, display: 'block' }}>
          {block.vetId === null ? 'Clinic Closed' : 'Blocked'}
        </Typography>
        <Typography variant="caption" sx={{ color: '#b71c1c', fontSize: '0.6rem', lineHeight: 1.1, display: 'block' }}>
          {startStr} - {endStr}
        </Typography>
        {block.reason && position.height > 45 && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: '0.55rem', lineHeight: 1.1, display: 'block', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {block.reason}
          </Typography>
        )}
      </Box>
    );
  };

  const handleTimeSlotClick = (date: Date, hour: number, minute: number, providerId?: string) => {
    const clickedDate = new Date(date);
    clickedDate.setHours(hour, minute, 0, 0);
    setSelectedDialogDate(clickedDate);
    
    // Set the provider ID for the dialog to pre-select the veterinarian
    if (providerId && providerId !== 'unassigned') {
      setSelectedVetForDialog(providerId);
    } else {
      setSelectedVetForDialog(''); // Leave unassigned
    }

    // An empty slot is a large click target with no label, so there is no button to hide here —
    // the click itself must do nothing (INV-108). Opening a booking form that refuses on save is
    // the same defect as the Add button, and more expensive: the form is long.
    if (!can.addFromSlot) return;
    setIsAddDialogOpen(true);
  };

  const handleViewTypeChange = (newViewType: ViewType) => {
    setViewType(newViewType);
  };

  const openDay = (day: Date) => {
    setCurrentDate(day);
    setViewType('daily');
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewType === 'daily') {
      if (direction === 'prev') {
        newDate.setDate(newDate.getDate() - 1);
      } else {
        newDate.setDate(newDate.getDate() + 1);
      }
    } else if (viewType === 'weekly') {
      // Weekly navigation
      if (direction === 'prev') {
        newDate.setDate(newDate.getDate() - 7);
      } else {
        newDate.setDate(newDate.getDate() + 7);
      }
    } else {
      // Step to the 1st: setMonth on the 31st overflows (Oct 31 + 1 month = Dec 1) and skips a month.
      setCurrentDate(new Date(newDate.getFullYear(), newDate.getMonth() + (direction === 'prev' ? -1 : 1), 1));
      return;
    }
    setCurrentDate(newDate);
  };

  // Today keeps the view and provider you are on.
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleCalendarPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCalendarPopoverAnchor(event.currentTarget);
  };

  const handleCalendarPopoverClose = () => {
    setCalendarPopoverAnchor(null);
  };

  const handleDateSelect = (date: Date) => {
    setCurrentDate(date);
    handleCalendarPopoverClose();
  };

  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      await executeDelete({
        entityType: 'appointment',
        table: 'appointments',
        entityId: selectedAppointment.id,
        networkFn: () => api.delete(`/appointments/${selectedAppointment.id}`),
      });
      trackFeatureUsed('appointment_deleted');
      setSnackbar({
        open: true,
        message: 'Appointment deleted successfully',
        severity: 'success',
      });
      setIsDeleteDialogOpen(false);
      setSelectedAppointment(null);
      await queryClient.invalidateQueries(['appointments']);
      await queryClient.invalidateQueries('appointments');
      await queryClient.refetchQueries(['appointments']);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      setSnackbar({
        open: true,
        message: error instanceof OfflineWriteBlockedError
          ? error.message
          : 'Failed to delete appointment. Please try again.',
        severity: 'error',
      });
    }
  };

  // Shared: execute the actual PUT after equipment confirmation (or immediately if no equipment)
  /**
   * Shared appointment-update path (edit dialog, drag, equipment confirm).
   * Routes through executeUpdate so offline edits queue to /sync/push with
   * baseUpdatedAt conflict detection. Equipment reservations are ONLINE-ONLY
   * (the push handler has no window-confirmation step and strips the
   * `equipment` key server-side anyway) — offline we drop the key explicitly
   * and tell the caller, so existing reservations stay untouched and the user
   * is told nothing was booked/changed.
   * Returns true when equipment changes were dropped because we're offline.
   */
  const executeAppointmentUpdate = async (
    appointmentId: string,
    data: any,
    { refresh = true }: { refresh?: boolean } = {},
  ): Promise<boolean> => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hasEquipment = data?.equipment !== undefined;
    // The QUEUED payload never carries equipment: reservations are online-only
    // (no server-side window confirmation on replay), and this also covers the
    // connectivity-fallthrough where the online PUT fails and the op falls
    // back to the queue. The online networkFn sends the full payload.
    const { equipment: _omitted, ...queuedPayload } = data ?? {};
    const equipmentDropped = hasEquipment && !skipWhenOffline();
    const networkFn = () => api.put(`/appointments/${appointmentId}`, { ...data, timeZone }).then(r => r.data);
    if (data?.barnId !== undefined) {
      // /sync strips barnId, so a barn change must not fall back to the queue when the connection drops.
      await networkFn();
    } else {
      await executeUpdate({
        entityType: 'appointment',
        table: 'appointments',
        entityId: appointmentId,
        payload: queuedPayload,
        networkFn,
      });
    }
    if (refresh) {
      await queryClient.invalidateQueries(['appointments']);
      await queryClient.invalidateQueries('appointments');
      await queryClient.refetchQueries(['appointments']);
    }
    return equipmentDropped;
  };

  const updateSuccessMessage = (equipmentDropped: boolean) =>
    equipmentDropped
      ? "Appointment updated — you're offline, so equipment reservations were left unchanged."
      : 'Appointment updated successfully';

  // Called when user confirms equipment window adjustments
  const handleEquipConfirm = async () => {
    if (!pendingUpdate) return;
    try {
      const dropped = await executeAppointmentUpdate(pendingUpdate.appointmentId, {
        ...pendingUpdate.data,
        equipment: equipConfirmWindows.map(w => ({
          id: w.itemId,
          startTime: w.startTime.toISOString(),
          endTime: w.endTime.toISOString(),
        })),
      });
      setSnackbar({ open: true, message: updateSuccessMessage(dropped), severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err instanceof OfflineWriteBlockedError
            ? err.message
            : err?.response?.data?.message || 'Failed to update appointment.',
        severity: 'error',
      });
    } finally {
      setEquipConfirmOpen(false);
      setPendingUpdate(null);
      setSelectedAppointment(null);
      setIsEditMode(false);
    }
  };

  // Called when user skips equipment adjustment
  const handleEquipSkip = async () => {
    if (!pendingUpdate) return;
    try {
      await executeAppointmentUpdate(pendingUpdate.appointmentId, pendingUpdate.data);
      setSnackbar({ open: true, message: 'Appointment updated successfully', severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message:
          err instanceof OfflineWriteBlockedError
            ? err.message
            : err?.response?.data?.message || 'Failed to update appointment.',
        severity: 'error',
      });
    } finally {
      setEquipConfirmOpen(false);
      setPendingUpdate(null);
      setSelectedAppointment(null);
      setIsEditMode(false);
    }
  };

  // Shared: after conflict check passes, either show equipment dialog or update immediately
  const proceedWithUpdate = (
    appointmentId: string,
    updateData: any,
    appointment: Appointment,
    oldDateISO: string,
    newDateISO: string,
    newDuration: number,
  ) => {
    const schedules = appointment.equipmentSchedules ?? [];
    const dateChanged = new Date(oldDateISO).getTime() !== new Date(newDateISO).getTime();
    // The shift-confirm dialog exists to MOVE untouched reservations along
    // with a date change. Skip it when: there are no reservations; the user
    // explicitly set windows in the edit form (updateData.equipment — the
    // dialog would clobber them with delta-shifted originals); the date didn't
    // change; or we're offline (equipment writes are online-only — the
    // reservations stay put and the snackbar says so).
    if (
      schedules.length === 0 ||
      updateData.equipment !== undefined ||
      !dateChanged ||
      !skipWhenOffline()
    ) {
      executeAppointmentUpdate(appointmentId, updateData)
        .then((dropped) => {
          setSnackbar({
            open: true,
            message: updateSuccessMessage(dropped),
            severity: 'success',
          });
          setSelectedAppointment(null);
          setIsEditMode(false);
        })
        .catch((err: any) =>
          setSnackbar({
            open: true,
            message:
              err instanceof OfflineWriteBlockedError
                ? err.message
                : err?.response?.data?.message || 'Failed to update appointment.',
            severity: 'error',
          }),
        );
      return;
    }

    // Shift equipment windows by the same delta as the appointment
    const oldMs = new Date(oldDateISO).getTime();
    const newMs = new Date(newDateISO).getTime();
    const deltaMs = newMs - oldMs;

    const shifted = schedules.map(s => ({
      itemId: s.equipmentId,
      name: s.equipment.name,
      ...shiftEquipmentWindow(s, deltaMs, new Date(newDateISO)),
    }));

    setPendingUpdate({ appointmentId, data: updateData });
    setEquipConfirmWindows(shifted);
    setEquipConfirmOpen(true);
  };

  const handleEditAppointment = () => {
    if (!selectedAppointment) return;

    setEditFormData({
      date: selectedAppointment.date,
      duration: selectedAppointment.duration || 60,
      type: selectedAppointment.type,
      location: selectedAppointment.location,
      notes: selectedAppointment.notes || '',
      assignedVetId: selectedAppointment.assignedVetId || '',
      status: normalizeStatus(selectedAppointment.status),
      barnId: selectedAppointment.barnId || '',
    });

    // Pre-populate equipment from existing schedules. A reservation spanning
    // its whole calendar day re-opens with "All day" checked; its fallback
    // window is seeded from the appointment itself so unchecking the box
    // reveals sensible pickers instead of the midnight span.
    const schedules = selectedAppointment.equipmentSchedules ?? [];
    setEditEquipmentIds(schedules.map(s => s.equipmentId));
    const windows: Record<string, { startTime: Date; endTime: Date }> = {};
    const allDay: Record<string, boolean> = {};
    const apptStart = new Date(selectedAppointment.date);
    const apptEnd = new Date(apptStart.getTime() + (selectedAppointment.duration || 60) * 60_000);
    schedules.forEach(s => {
      const start = new Date(s.startTime);
      const end = new Date(s.endTime);
      if (isAllDayWindow(start, end)) {
        allDay[s.equipmentId] = true;
        windows[s.equipmentId] = { startTime: apptStart, endTime: apptEnd };
      } else {
        windows[s.equipmentId] = { startTime: start, endTime: end };
      }
    });
    setEditEquipmentWindows(windows);
    setEditEquipmentAllDay(allDay);
    setEditEquipmentDirty(false);

    setIsEditMode(true);
  };

  /** The live horses of this appointment's barn visit; Edit changes them together when there are two or more. */
  const liveVisitHorses = (apt: Appointment) =>
    apt.visitGroupId && !isCancelled(apt.status)
      ? appointments.filter((a) => a.visitGroupId === apt.visitGroupId && !isCancelled(a.status))
      : [];

  const handleSaveEdit = async () => {
    if (!selectedAppointment) return;
    const visitHorses = liveVisitHorses(selectedAppointment);
    if (visitHorses.length > 1) {
      await saveVisitEdit(selectedAppointment, visitHorses);
      return;
    }
    // /sync strips barnId, so a queued barn change would be dropped without a word.
    const barnChanged = !!editFormData.barnId && editFormData.barnId !== (selectedAppointment.barnId || '');
    if (barnChanged && !skipWhenOffline()) {
      setSnackbar({ open: true, message: 'Changing the barn needs a connection.', severity: 'error' });
      return;
    }

    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Check for conflicts before updating (online-only — the check endpoint is
      // not replicated, so skip it offline and let the edit proceed). Flag-off,
      // skipWhenOffline() is always true: the check runs unconditionally.
      if (skipWhenOffline()) {
        const conflictCheck = await appointmentService.checkConflicts(selectedAppointment.id, {
          newDate: editFormData.date,
          newDuration: editFormData.duration,
          newAssignedVetId: editFormData.assignedVetId || undefined,
          timeZone,
        });

        if (conflictCheck.hasConflict) {
          const conflicts = conflictCheck.conflicts;
          let errorMessage = 'Cannot update appointment due to conflicts:\n\n';

          conflicts.forEach((conflict: any) => {
            errorMessage += `• ${conflict.message}\n`;
          });

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
          return;
        }
      }

      // No conflicts — check equipment and proceed
      trackFeatureUsed('appointment_edited');
      const updateData = {
        date: editFormData.date,
        duration: editFormData.duration,
        type: editFormData.type,
        location: editFormData.location,
        notes: editFormData.notes || undefined,
        assignedVetId: editFormData.assignedVetId || null,
        ...(barnChanged ? { barnId: editFormData.barnId } : {}),
        // Only a status the person changed: an unchanged one may be a spelling we read as an alias
        // (iOS's in_progress shows as Checked in) and must not be rewritten.
        ...(editFormData.status !== normalizeStatus(selectedAppointment.status) ? { status: editFormData.status } : {}),
        // Only when the user touched equipment — an unconditional list from a
        // replica-hydrated row (no schedules loaded) would wipe real
        // reservations server-side.
        ...(editEquipmentDirty
          ? {
              // buildEquipmentPayload returns undefined for an empty selection
              // (create semantics: omit the key); the edit save must still send
              // [] so "remove all equipment" wipes the reservations server-side.
              equipment:
                buildEquipmentPayload(
                  editEquipmentIds,
                  editEquipmentWindows,
                  editEquipmentAllDay,
                  new Date(editFormData.date),
                  new Date(new Date(editFormData.date).getTime() + editFormData.duration * 60_000),
                ) ?? [],
            }
          : {}),
      };
      proceedWithUpdate(
        selectedAppointment.id,
        updateData,
        selectedAppointment,
        selectedAppointment.date,
        editFormData.date,
        editFormData.duration,
      );
    } catch (error) {
      console.error('Error updating appointment:', error);
      setSnackbar({
        open: true,
        message: error instanceof OfflineWriteBlockedError
          ? error.message
          : 'Failed to update appointment. Please try again.',
        severity: 'error',
      });
    }
  };

  /**
   * Edit on a barn visit horse: a new start moves every horse by the same amount, and a new length
   * or vet applies to every horse (moveVisitHorses). Type, status, notes and equipment are this
   * horse's own, saved after the move.
   */
  const saveVisitEdit = async (selected: Appointment, horses: Appointment[]) => {
    // The panel's selection is a snapshot the move's refetch never updates; measured from it, a
    // retry after a failed second write would shift every horse again.
    const apt = horses.find((h) => h.id === selected.id) ?? selected;
    const vetId = editFormData.assignedVetId || null;
    const moved = await moveVisitHorses(
      apt.visitGroupId!,
      horses,
      {
        deltaMs: new Date(editFormData.date).getTime() - new Date(apt.date).getTime(),
        vetId: vetId !== (apt.assignedVetId ?? null) ? vetId : undefined,
        duration: editFormData.duration !== (apt.duration ?? 60) ? editFormData.duration : undefined,
      },
      'Updated',
    );
    if (moved === 'refused') return;
    if (moved === 'partial') {
      // Some horses moved and some did not; the message sends them to the visit page to finish.
      setSelectedAppointment(null);
      setIsEditMode(false);
      return;
    }
    const own = {
      ...(editFormData.type !== apt.type ? { type: editFormData.type } : {}),
      ...(editFormData.notes !== (apt.notes || '') ? { notes: editFormData.notes } : {}),
      ...(editFormData.status !== normalizeStatus(apt.status) ? { status: editFormData.status } : {}),
      ...(editEquipmentDirty
        ? {
            equipment:
              buildEquipmentPayload(
                editEquipmentIds,
                editEquipmentWindows,
                editEquipmentAllDay,
                new Date(editFormData.date),
                new Date(new Date(editFormData.date).getTime() + editFormData.duration * 60_000),
              ) ?? [],
          }
        : {}),
    };
    try {
      if (Object.keys(own).length > 0) {
        const dropped = await executeAppointmentUpdate(apt.id, own);
        setSnackbar({ open: true, message: updateSuccessMessage(dropped), severity: 'success' });
      }
      trackFeatureUsed('appointment_edited');
      setSelectedAppointment(null);
      setIsEditMode(false);
    } catch (err: any) {
      const reason = err instanceof OfflineWriteBlockedError ? err.message : err?.response?.data?.message || 'Failed to update appointment.';
      setSnackbar({
        open: true,
        message: moved === 'done' ? `The visit's time and vet were saved. This horse's other changes were not: ${reason}` : reason,
        severity: 'error',
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({ date: '', duration: 60, type: '', location: '', notes: '', assignedVetId: '', status: 'scheduled', barnId: '' });
    setEditEquipmentIds([]);
    setEditEquipmentWindows({});
    setEditEquipmentAllDay({});
    setEditEquipmentDirty(false);
  };

  /** Status chips and Cancel: one PUT of `status` (queued offline like any edit). */
  const handleStatusChange = async (status: AppointmentStatus) => {
    const apt = selectedAppointment;
    if (!apt) return;
    try {
      await executeAppointmentUpdate(apt.id, { status }, { refresh: false });
      setSelectedAppointment((current) => (current?.id === apt.id ? { ...current, status } : current));
      setSnackbar({
        open: true,
        message: status === 'cancelled' ? 'Appointment cancelled. It stays on record.' : `${statusStyle(status).label}.`,
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error instanceof OfflineWriteBlockedError
          ? error.message
          : error?.response?.data?.message || 'Could not change the status. Please try again.',
        severity: 'error',
      });
    } finally {
      await queryClient.invalidateQueries(['appointments']);
    }
  };

  /**
   * Move a barn visit's horses together: each keeps its offset from the others, and an explicit
   * vet or length applies to every horse (`undefined` keeps each horse's own). There is no group
   * endpoint, so each horse goes through the same check-conflicts + PUT path as a single drag;
   * clashes between the visit's own horses (they share one window by design) are ignored, and each
   * horse's equipment window moves with it. Used by dragging the block and by the panel's Edit.
   * 'refused' wrote nothing; 'partial' wrote some horses before an error.
   */
  const moveVisitHorses = async (
    visitId: string,
    members: Appointment[],
    change: { deltaMs: number; vetId?: string | null; duration?: number },
    verb: 'Moved' | 'Updated',
  ): Promise<'unchanged' | 'done' | 'refused' | 'partial'> => {
    const { deltaMs } = change;
    const vetOf = (apt: Appointment) => (change.vetId !== undefined ? change.vetId : apt.assignedVetId ?? null);
    const lengthOf = (apt: Appointment) => change.duration ?? apt.duration ?? 60;
    const vetChanged = members.some((apt) => vetOf(apt) !== (apt.assignedVetId ?? null));
    const lengthChanged = members.some((apt) => lengthOf(apt) !== (apt.duration ?? 60));
    if (deltaMs === 0 && !vetChanged && !lengthChanged) return 'unchanged';
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const fail = (message: string) => {
      setSnackbar({ open: true, message, severity: 'error' });
      return 'refused' as const;
    };
    const online = skipWhenOffline();
    const hasEquipment = members.some((apt) => (apt.equipmentSchedules?.length ?? 0) > 0);
    // Each horse holds the same equipment window; handing them to another vet one at a time would
    // briefly put two vets on one unit, which the server refuses part-way through the visit.
    if (vetChanged && hasEquipment && members.length > 1) {
      return fail('This barn visit has equipment booked. Change its vet from the barn visit page.');
    }
    const shift = (iso: string) => new Date(new Date(iso).getTime() + deltaMs).toISOString();

    if (online) {
      try {
        for (const apt of members) {
          if ((apt as any)._pendingSync) continue;
          const { conflicts } = await appointmentService.checkConflicts(apt.id, {
            newDate: shift(apt.date),
            newDuration: lengthOf(apt),
            newAssignedVetId: vetOf(apt),
            timeZone: zone,
          });
          const outside = (conflicts ?? []).filter(
            (c) => !c.conflictingAppointment || c.conflictingAppointment.visitGroupId !== visitId,
          );
          if (outside.length > 0) {
            return fail(`Cannot ${verb === 'Moved' ? 'move' : 'change'} the barn visit:\n\n${outside.map((c) => `• ${c.message}`).join('\n')}`);
          }
        }
      } catch (error: any) {
        return fail(error?.response?.data?.message || 'Failed to check conflicts. Please try again.');
      }
    }

    let moved = 0;
    try {
      for (const apt of members) {
        await executeAppointmentUpdate(
          apt.id,
          {
            date: shift(apt.date),
            duration: lengthOf(apt),
            type: apt.type,
            location: apt.location,
            notes: apt.notes,
            assignedVetId: vetOf(apt),
            ...(online && apt.equipmentSchedules?.length
              ? {
                  equipment: apt.equipmentSchedules.map((s) => {
                    const { startTime, endTime } = shiftEquipmentWindow(s, deltaMs, new Date(shift(apt.date)));
                    return { id: s.equipmentId, startTime: startTime.toISOString(), endTime: endTime.toISOString() };
                  }),
                }
              : {}),
          },
          { refresh: false },
        );
        moved += 1;
      }
      setSnackbar({
        open: true,
        message: `${verb} the barn visit (${members.length} horses)${
          !hasEquipment ? '.' : online ? ' and its equipment.' : ". You're offline, so equipment reservations were left unchanged."
        }`,
        severity: 'success',
      });
      return 'done';
    } catch (error: any) {
      fail(
        `${verb} ${moved} of ${members.length} horses. ${
          error instanceof OfflineWriteBlockedError ? error.message : error?.response?.data?.message || 'The rest could not be changed.'
        } Open the barn visit to finish.`,
      );
      return moved > 0 ? 'partial' : 'refused';
    } finally {
      await queryClient.invalidateQueries(['appointments']);
    }
  };

  /** Drag a grouped barn visit: the horses in that column move to the drop's time and vet. */
  const moveVisitGroup = async (
    dragId: string,
    drop: { providerId: string; hour: number; minute: number; date?: Date },
  ) => {
    const [visitId, fromProvider, fromDay] = dragId.slice('visit:'.length).split('|');
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const members = appointments.filter(
      (apt) =>
        apt.visitGroupId === visitId &&
        !isCancelled(apt.status) &&
        (apt.assignedVetId || 'unassigned') === fromProvider &&
        DateTime.fromISO(apt.date, { zone: 'utc' }).setZone(zone).toFormat('yyyy-MM-dd') === fromDay,
    );
    if (members.length === 0) return;
    const target = new Date(drop.date || currentDate);
    target.setHours(drop.hour, drop.minute, 0, 0);
    const deltaMs = target.getTime() - Math.min(...members.map((apt) => new Date(apt.date).getTime()));
    await moveVisitHorses(visitId, members, { deltaMs, vetId: drop.providerId === 'unassigned' ? null : drop.providerId }, 'Moved');
  };

  // Drag and drop state and handlers
  const [isDragging, setIsDragging] = useState(false);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before activating drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setIsDragging(false);

    // Belt as well as braces (INV-108). `useDraggable({ disabled })` already stops the drag from
    // starting, but this is the handler that WRITES — and the two are set in different components,
    // so a future call site that forgets the prop would otherwise reach the PUT.
    if (!can.edit) return;

    const { active, over } = event;

    if (!over || !active) {
      return;
    }

    // Handle drops in both daily and weekly views
    if (viewType !== 'daily' && viewType !== 'weekly') {
      return;
    }

    const appointmentId = active.id as string;
    if (appointmentId.startsWith('visit:')) {
      const drop = over.data.current as { providerId: string; hour: number; minute: number; date?: Date } | undefined;
      if (drop) await moveVisitGroup(appointmentId, drop);
      return;
    }
    const appointment = appointments.find((apt) => apt.id === appointmentId);

    if (!appointment) {
      return;
    }

    // Parse drop zone data
    const dropData = over.data.current as { providerId: string; hour: number; minute: number; date?: Date };
    
    if (!dropData) {
      return;
    }

    // Calculate new date/time
    // In weekly view, use the date from the drop zone; in daily view, use currentDate
    const baseDate = dropData.date || currentDate;
    const newDate = new Date(baseDate);
    newDate.setHours(dropData.hour, dropData.minute, 0, 0);

    // Check if anything actually changed
    const oldVetId = appointment.assignedVetId || 'unassigned';
    const newVetId = dropData.providerId === 'unassigned' ? null : dropData.providerId;
    
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const oldDateTime = DateTime.fromISO(appointment.date, { zone: 'utc' }).setZone(userTimeZone);
    const newDateTime = DateTime.fromJSDate(newDate, { zone: userTimeZone });

    // Check if date, time, or vet changed
    const dateChanged = oldDateTime.toFormat('yyyy-MM-dd') !== newDateTime.toFormat('yyyy-MM-dd');
    const timeChanged = oldDateTime.hour !== newDateTime.hour || oldDateTime.minute !== newDateTime.minute;
    const vetChanged = oldVetId !== (newVetId || 'unassigned');

    if (!dateChanged && !timeChanged && !vetChanged) {
      return; // Nothing changed
    }

    // Check for conflicts before updating. Online-only — the /check-conflicts
    // endpoint isn't replicated, so skip it offline (mirrors handleSaveEdit) and
    // also skip it for a not-yet-synced optimistic row (temp id would 404 in the
    // reconnect race before the temp→server id swap). The move still proceeds via
    // proceedWithUpdate → executeAppointmentUpdate (executeUpdate op queue).
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (skipWhenOffline() && !(appointment as any)._pendingSync) {
        const conflictCheck = await appointmentService.checkConflicts(appointmentId, {
          newDate: newDate.toISOString(),
          newDuration: appointment.duration || 60,
          newAssignedVetId: newVetId,
          timeZone,
        });

        if (conflictCheck.hasConflict) {
          const conflicts = conflictCheck.conflicts;
          let errorMessage = 'Cannot move appointment due to conflicts:\n\n';

          conflicts.forEach((conflict: any) => {
            errorMessage += `• ${conflict.message}\n`;
          });

          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
          return;
        }
      }

      // No conflicts — check equipment and proceed
      const dragUpdateData = {
        date: newDate.toISOString(),
        duration: appointment.duration,
        type: appointment.type,
        location: appointment.location,
        notes: appointment.notes,
        assignedVetId: newVetId,
      };
      proceedWithUpdate(
        appointmentId,
        dragUpdateData,
        appointment,
        appointment.date,
        newDate.toISOString(),
        appointment.duration || 60,
      );
    } catch (error: any) {
      console.error('Error checking conflicts:', error);
      setSnackbar({
        open: true,
        message: error instanceof OfflineWriteBlockedError
          ? error.message
          : (error?.response?.data?.message || 'Failed to check conflicts. Please try again.'),
        severity: 'error',
      });
    }
  };

  if (isLoading || isLoadingVets) {
    return (
      <Box sx={{ 
        height: { xs: 'auto', md: '100vh' }, 
        minHeight: { xs: '100vh', md: 'auto' },
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden' 
      }}>
        <Box p={2} sx={{ borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box sx={{ width: 200, height: 32, bgcolor: 'grey.200', borderRadius: 1 }} />
            <Box sx={{ width: 150, height: 40, bgcolor: 'grey.200', borderRadius: 1 }} />
          </Box>
          <Box sx={{ width: '100%', height: 40, bgcolor: 'grey.100', borderRadius: 1 }} />
        </Box>
        
        <Box p={3} display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Box textAlign="center">
            <CircularProgress size={60} sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading appointments...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Preparing your daily view
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={startEdgeCollision}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        overflowX: 'hidden',
        height: '100vh',
      }}>
        {/* Top bar, toolbar and counts (design: the Schedule canvas). */}
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 1.5, sm: 2.5 },
            pt: { xs: 1.5, sm: 2 },
            pb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
            <Box>
              <Typography component="h1" sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                Schedule
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {timeZoneLabel}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <TextField
                size="small"
                placeholder="Search patient, owner or barn"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{ 'aria-label': 'Search the schedule' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
                sx={{ width: { xs: '100%', sm: 280 }, bgcolor: 'background.paper' }}
              />
              <Button
                variant="outlined"
                startIcon={<EventBusyIcon />}
                onClick={(e) => setBlockMenuAnchor(e.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(blockMenuAnchor)}
                sx={{ textTransform: 'none', fontWeight: 500, height: 40, color: 'text.primary', borderColor: 'divider' }}
              >
                Block time
              </Button>
              {can.add && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  data-tour="appointment-add-button"
                  onClick={() => {
                    setSelectedDialogDate(new Date(currentDate));
                    setSelectedDuration(60);
                    setIsAddDialogOpen(true);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600, height: 40, boxShadow: 'none' }}
                >
                  New appointment
                </Button>
              )}
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{ p: 1, borderRadius: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <IconButton aria-label={`Previous ${viewUnit}`} onClick={() => navigateDate('prev')} sx={toolbarIconSx}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Button
                variant="outlined"
                title="Go to Today"
                onClick={goToToday}
                sx={{ textTransform: 'none', fontWeight: 500, height: 36, color: 'text.primary', borderColor: 'divider' }}
              >
                Today
              </Button>
              <IconButton aria-label={`Next ${viewUnit}`} onClick={() => navigateDate('next')} sx={toolbarIconSx}>
                <ChevronRight fontSize="small" />
              </IconButton>
              <Button
                onClick={handleCalendarPopoverOpen}
                endIcon={<ExpandMore />}
                aria-label={`${dateLabel}, pick a date`}
                sx={{ textTransform: 'none', fontSize: '1rem', fontWeight: 600, color: 'text.primary', ml: 0.5 }}
              >
                {dateLabel}
              </Button>
            </Box>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewType}
              onChange={(_, value) => value && handleViewTypeChange(value)}
              aria-label="Calendar view"
              sx={segmentedSx}
            >
              <ToggleButton value="daily">Day</ToggleButton>
              <ToggleButton value="weekly">Week</ToggleButton>
              <ToggleButton value="monthly">Month</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={columnMode}
                onChange={(_, value) => value && setColumnMode(value)}
                aria-label="Columns"
                sx={segmentedSx}
              >
                <ToggleButton value="providers">Providers</ToggleButton>
                <ToggleButton value="equipment">Equipment</ToggleButton>
              </ToggleButtonGroup>
              {viewType !== 'monthly' && !isMobile && !(showEquipment && viewType === 'weekly') && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={timeInterval}
                  onChange={(_, value) => value && changeSlotSize(value)}
                  aria-label="Time slot size in minutes"
                  sx={segmentedSx}
                >
                  <ToggleButton value={15}>15</ToggleButton>
                  <ToggleButton value={30}>30</ToggleButton>
                  <ToggleButton value={60}>60 min</ToggleButton>
                </ToggleButtonGroup>
              )}
              {/* The equipment board shows every provider's holds, or "Free until" would lie. */}
              {!showEquipment && (
                <Button
                  variant="outlined"
                  onClick={(e) => setProviderMenuAnchor(e.currentTarget)}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(providerMenuAnchor)}
                  endIcon={<ExpandMore />}
                  aria-label={`Showing ${providerFilterLabel}. Change providers`}
                  sx={{ textTransform: 'none', fontWeight: 500, height: 36, color: 'text.primary', borderColor: 'divider', gap: 0.5 }}
                >
                  <Box component="span" sx={{ display: 'inline-flex', mr: 0.75 }}>
                    {providerAvatars.map((vet, i) => (
                      <Box
                        component="span"
                        key={vet.id}
                        sx={{
                          width: 24,
                          height: 24,
                          ml: i === 0 ? 0 : -1,
                          borderRadius: '50%',
                          border: '2px solid',
                          borderColor: 'background.paper',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          bgcolor: providerColor[vet.id]?.bg ?? '#EFE7DC',
                          color: providerColor[vet.id]?.fg ?? '#3B2E1A',
                        }}
                      >
                        {vet.id === 'unassigned' ? '?' : providerInitials(vet.name)}
                      </Box>
                    ))}
                  </Box>
                  {providerFilterLabel}
                </Button>
              )}
              <Tooltip title="Calendar settings" arrow>
                <IconButton aria-label="Calendar settings" onClick={() => setIsSettingsOpen(true)} sx={toolbarIconSx}>
                  <SettingsOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {showEquipment ? (
              <>
                <Chip size="small" label={`${equipmentUnits.length} piece${equipmentUnits.length === 1 ? '' : 's'} of equipment`} sx={{ fontWeight: 500 }} />
                <Chip size="small" label={`${equipmentCounts.bookings} booking${equipmentCounts.bookings === 1 ? '' : 's'}`} sx={{ fontWeight: 500 }} />
                {equipmentCounts.offsite > 0 && (
                  <Chip size="small" label={`${equipmentCounts.offsite} leave${equipmentCounts.offsite === 1 ? 's' : ''} the clinic`} sx={{ fontWeight: 600, bgcolor: '#FCF0E3', color: '#7A4314' }} />
                )}
              </>
            ) : (
            <>
            <Chip size="small" label={`${rangeCounts.total} appointment${rangeCounts.total === 1 ? '' : 's'}`} sx={{ fontWeight: 500 }} />
            {rangeCounts.barnVisits > 0 && (
              <Chip size="small" label={`${rangeCounts.barnVisits} barn visit${rangeCounts.barnVisits === 1 ? '' : 's'}`} sx={{ fontWeight: 500 }} />
            )}
            {rangeCounts.unassigned > 0 && (
              <Chip
                size="small"
                clickable
                onClick={() => setSelectedProviderId('unassigned')}
                label={`${rangeCounts.unassigned} unassigned`}
                sx={{ fontWeight: 600, bgcolor: '#FCF0E3', color: '#7A4314', '&:hover': { bgcolor: '#F8E3CC' } }}
              />
            )}
            </>
            )}
            {search && (
              <Chip size="small" onDelete={() => setSearch('')} label={`Matching "${search}"`} sx={{ fontWeight: 500 }} />
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              {showEquipment
                ? viewType === 'daily'
                  ? `Color shows which provider has it.${can.addFromSlot ? ' Click an open slot to book that unit.' : ''}`
                  : viewType === 'weekly'
                    ? 'Color shows which provider has it. Click a day to open it.'
                    : 'Units booked each day. Click a day to open it.'
                : `Hover an appointment for details. Click to open.${can.edit ? ' Drag to move.' : ''}`}
            </Typography>
          </Box>

          <Menu anchorEl={providerMenuAnchor} open={Boolean(providerMenuAnchor)} onClose={() => setProviderMenuAnchor(null)}>
            {[{ id: null as string | null, name: 'All providers' }, { id: 'unassigned', name: 'Unassigned' }, ...veterinarians]
              .map((option) => (
                <MenuItem
                  key={option.id ?? 'all'}
                  selected={selectedProviderId === option.id}
                  onClick={() => {
                    setSelectedProviderId(option.id);
                    setProviderMenuAnchor(null);
                  }}
                  sx={{ gap: 1.25, minWidth: 220 }}
                >
                  {option.id && (
                    <Box
                      component="span"
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        bgcolor: providerColor[option.id]?.bg ?? 'background.paper',
                        color: providerColor[option.id]?.fg ?? '#7A4314',
                        border: option.id === 'unassigned' ? '1px dashed #C9772B' : 0,
                      }}
                    >
                      {option.id === 'unassigned' ? '?' : providerInitials(option.name)}
                    </Box>
                  )}
                  {option.name}
                </MenuItem>
              ))}
          </Menu>

          <Menu anchorEl={blockMenuAnchor} open={Boolean(blockMenuAnchor)} onClose={() => setBlockMenuAnchor(null)}>
            {veterinarians.map((vet) => (
              <MenuItem
                key={vet.id}
                onClick={() => {
                  setTimeBlockVet(vet);
                  setEditingTimeBlock(null);
                  setIsTimeBlockDialogOpen(true);
                  setBlockMenuAnchor(null);
                }}
                sx={{ minWidth: 220 }}
              >
                {vet.name}
              </MenuItem>
            ))}
            {canManageClosures && (
              <MenuItem
                onClick={() => {
                  setTimeBlockVet(null);
                  setEditingTimeBlock(null);
                  setIsTimeBlockDialogOpen(true);
                  setBlockMenuAnchor(null);
                }}
                sx={{ fontWeight: 600 }}
              >
                Close the whole clinic
              </MenuItem>
            )}
          </Menu>

          {/* Mini Calendar Popover */}
          <Popover
            open={Boolean(calendarPopoverAnchor)}
            anchorEl={calendarPopoverAnchor}
            onClose={handleCalendarPopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 2,
                width: 280
              }
            }}
          >
            <Box>
              {/* Calendar Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <IconButton
                  size="small"
                  aria-label="Previous month"
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {format(currentDate, 'MMMM yyyy')}
                </Typography>
                
                <IconButton
                  size="small"
                  aria-label="Next month"
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>

              {/* Days of Week Header */}
              <Grid container spacing={0} sx={{ mb: 1 }}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                  <Grid item xs key={i}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        fontWeight: 600,
                        color: 'text.secondary',
                        py: 0.5
                      }}
                    >
                      {day}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar Grid */}
              <Box>
                {(() => {
                  const year = currentDate.getFullYear();
                  const month = currentDate.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday-first, like the grids
                  const daysInMonth = lastDay.getDate();

                  const weeks = [];
                  let week = [];
                  
                  // Add empty cells for days before the first day of the month
                  for (let i = 0; i < firstDayOfWeek; i++) {
                    week.push(null);
                  }
                  
                  // Add all days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    week.push(day);
                    
                    // If week is complete (7 days), add it to weeks array
                    if (week.length === 7) {
                      weeks.push(week);
                      week = [];
                    }
                  }
                  
                  // Add empty cells for remaining days in the last week
                  while (week.length < 7 && week.length > 0) {
                    week.push(null);
                  }
                  if (week.length > 0) {
                    weeks.push(week);
                  }

                  return weeks.map((week, weekIndex) => (
                    <Grid container spacing={0} key={weekIndex}>
                      {week.map((day, dayIndex) => {
                        const isCurrentDay = day === currentDate.getDate() && 
                                           month === new Date().getMonth() && 
                                           year === new Date().getFullYear();
                        const isSelectedDay = day === currentDate.getDate();
                        
                        return (
                          <Grid item xs key={dayIndex}>
                            <Button
                              variant="text"
                              disabled={day === null}
                              onClick={() => day && handleDateSelect(new Date(year, month, day))}
                              sx={{
                                width: '100%',
                                height: 32,
                                minWidth: 32,
                                p: 0,
                                borderRadius: 1,
                                fontSize: '0.875rem',
                                fontWeight: isSelectedDay ? 600 : 400,
                                color: day === null ? 'transparent' : 
                                       isSelectedDay ? 'white' : 'text.primary',
                                backgroundColor: isSelectedDay ? '#C9772B' : 'transparent',
                                '&:hover': {
                                  backgroundColor: day === null ? 'transparent' :
                                               isSelectedDay ? '#C9772B' : 'rgba(0,0,0,0.04)'
                                }
                              }}
                            >
                              {day}
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ));
                })()}
              </Box>
            </Box>
          </Popover>
        </Box>

      {/* Calendar Grid. Column headers live INSIDE the scrolling area and share the body's grid
          tracks, so a scrollbar can never push the body out of line with its headers. */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', mx: { xs: 1.5, sm: 2.5 } }}>
        <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box ref={gridScrollRef} sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
            {showEquipment && (isOffline || equipmentUnits.length === 0) ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  {isOffline ? 'The equipment view needs a connection.' : isLoadingEquipment ? 'Loading equipment…' : 'No equipment yet.'}
                </Typography>
                {!isOffline && !isLoadingEquipment && (
                  <Typography variant="body2">Add units in Inventory, Equipment, and their bookings show here by provider.</Typography>
                )}
              </Box>
            ) : viewType === 'monthly' ? (
              <Box
                data-testid="month-grid"
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', minWidth: isMobile ? 560 : 0 }}
              >
                {WEEKDAY_LABELS.map((label) => (
                  <Box
                    key={label}
                    sx={{ position: 'sticky', top: 0, zIndex: 2, p: 1.25, textAlign: 'center', bgcolor: 'background.paper', borderBottom: '1px solid #E5E7EB', borderRight: '1px solid #E5E7EB' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
                  </Box>
                ))}
                {monthDays.map((day) => {
                  const dayKey = format(day, 'yyyy-MM-dd');
                  const inMonth = day.getMonth() === currentDate.getMonth();
                  const isToday = dayKey === todayLocalISO();
                  const counts = dayProviderCounts[dayKey] ?? {};
                  const bubbles = monthProviders
                    .map((provider) => ({ provider, n: counts[provider.id] ?? 0 }))
                    .filter((b) => b.n > 0);
                  const unitCounts = showEquipment
                    ? equipmentUnits
                        .map((unit) => ({ unit, n: equipmentByDay[dayKey]?.[unit.id]?.length ?? 0 }))
                        .filter((c) => c.n > 0)
                    : [];
                  const total = showEquipment
                    ? unitCounts.reduce((n, c) => n + c.n, 0)
                    : bubbles.reduce((n, b) => n + b.n, 0);
                  const noun = showEquipment ? 'booking' : 'appointment';
                  const dayBlocks = timeBlocks.filter((block: any) => {
                    const blockDay = DateTime.fromISO(block.startTime, { zone: 'utc' }).setZone(userTimeZone).toFormat('yyyy-MM-dd');
                    if (blockDay !== dayKey) return false;
                    // Closures show whatever the provider filter; a vet's block only under that vet.
                    if (selectedProviderId && selectedProviderId !== 'unassigned') {
                      return block.vetId === selectedProviderId || block.vetId === null;
                    }
                    return true;
                  });
                  // A closure has hours (often part of a day), so say which instead of "closed".
                  const closure = dayBlocks.find((block: any) => block.vetId === null);
                  const hour = (iso: string) =>
                    DateTime.fromISO(iso, { zone: 'utc' }).setZone(userTimeZone).toFormat('h:mm a').replace(':00', '');
                  const closed = closure ? `Closed ${hour(closure.startTime)} - ${hour(closure.endTime)}` : '';
                  return (
                    <Box
                      key={dayKey}
                      component="button"
                      type="button"
                      onClick={() => openDay(day)}
                      aria-label={`${format(day, 'EEEE, MMMM d')}: ${total} ${noun}${total === 1 ? '' : 's'}${closed ? `, ${closed}` : ''}. Open day`}
                      sx={{
                        height: isMobile ? 92 : 116,
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 0.75,
                        overflow: 'hidden',
                        textAlign: 'left',
                        font: 'inherit',
                        color: 'inherit',
                        cursor: 'pointer',
                        border: 0,
                        borderRight: '1px solid #eeeeee',
                        borderBottom: '1px solid #eeeeee',
                        bgcolor: inMonth ? 'background.paper' : '#f7f7f8',
                        '&:hover': { bgcolor: inMonth ? '#fafafa' : '#f0f0f2' },
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                      }}
                    >
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box
                          component="span"
                          sx={{
                            minWidth: 26,
                            height: 26,
                            px: 0.5,
                            boxSizing: 'border-box',
                            borderRadius: '13px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.8rem',
                            fontWeight: isToday ? 700 : inMonth ? 600 : 400,
                            bgcolor: isToday ? 'primary.main' : 'transparent',
                            color: isToday ? 'primary.contrastText' : inMonth ? 'text.primary' : 'text.secondary',
                          }}
                        >
                          {day.getDate() === 1 ? format(day, 'MMM d') : day.getDate()}
                        </Box>
                        {total > 0 && (
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{total}</Typography>
                        )}
                      </Box>
                      {closed ? (
                        <Box component="span" sx={{ alignSelf: 'flex-start', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.7rem', fontWeight: 600, color: '#b71c1c', bgcolor: 'rgba(211,47,47,0.08)' }}>
                          {closed}
                        </Box>
                      ) : dayBlocks.length > 0 ? (
                        <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#b71c1c' }}>
                          {dayBlocks.length} blocked
                        </Box>
                      ) : null}
                      <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {unitCounts.map(({ unit, n }) => (
                          <Box
                            component="span"
                            key={unit.id}
                            title={`${unit.name}: ${n}`}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.75, borderRadius: 999, border: '1px solid', borderColor: 'divider', fontSize: '0.72rem', fontWeight: 600, color: 'text.primary', bgcolor: '#F6F7F8', maxWidth: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                          >
                            {`${unit.name} ${n}`}
                          </Box>
                        ))}
                        {!showEquipment && bubbles.map(({ provider, n }) => {
                          const color = providerColor[provider.id];
                          const unassigned = provider.id === 'unassigned';
                          return (
                            <Box
                              component="span"
                              key={provider.id}
                              title={`${provider.name}: ${n}`}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.4,
                                pr: 0.75,
                                borderRadius: 999,
                                border: '1px solid',
                                borderColor: unassigned ? '#C9772B' : 'divider',
                                borderStyle: unassigned ? 'dashed' : 'solid',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.55rem',
                                  fontWeight: 700,
                                  bgcolor: color?.bg ?? 'background.paper',
                                  color: color?.fg ?? '#7A4314',
                                }}
                              >
                                {unassigned ? '?' : providerInitials(provider.name)}
                              </Box>
                              {n}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : showEquipment ? (
              viewType === 'weekly' ? (
                <EquipmentWeekGrid
                  units={equipmentUnits}
                  days={weekDays}
                  byDay={equipmentByDay}
                  todayKey={todayLocalISO()}
                  providerColor={providerColor}
                  onOpenAppointment={setSelectedAppointment}
                  onOpenDay={openDay}
                />
              ) : (
                <EquipmentDayGrid
                  units={equipmentUnits}
                  reservations={equipmentReservations}
                  timeSlots={timeSlots}
                  slotHeight={slotHeight}
                  timeInterval={timeInterval}
                  gridStartHour={gridStartHour}
                  gridEndHour={gridEndHour}
                  timeColWidth={timeColWidth}
                  minColWidth={isMobile ? 140 : 168}
                  headerHeight={HEADER_ROW_HEIGHT}
                  showsToday={showsToday}
                  nowTop={nowTop}
                  now={now}
                  providerColor={providerColor}
                  onOpenAppointment={setSelectedAppointment}
                  onSlotClick={
                    can.addFromSlot
                      ? (unitId, hour, minute) => {
                          setPreSelectedEquipmentId(unitId);
                          handleTimeSlotClick(currentDate, hour, minute);
                        }
                      : undefined
                  }
                />
              )
            ) : (
              <Box
                data-testid="time-grid"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `${timeColWidth}px repeat(${Math.max(gridColumns.length, 1)}, minmax(${minColWidth}px, 1fr))`,
                  minWidth: 'min-content',
                }}
              >
                <Box
                  sx={{
                    gridRow: isWeekSplit ? 'span 2' : undefined,
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    zIndex: 13,
                    display: 'flex',
                    alignItems: 'flex-end',
                    p: 1,
                    bgcolor: 'background.paper',
                    borderRight: '1px solid #E5E7EB',
                    borderBottom: '1px solid #E5E7EB',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Time</Typography>
                </Box>

                {viewType === 'daily'
                  ? gridColumns.map(({ key, provider }) => {
                      const unassigned = provider.id === 'unassigned';
                      const columnApts = (appointmentsByDay[format(currentDate, 'yyyy-MM-dd')] ?? []).filter((apt) =>
                        unassigned ? !apt.assignedVetId : apt.assignedVetId === provider.id,
                      );
                      const bookedMinutes = columnApts.reduce((n, apt) => n + (apt.duration || 60), 0);
                      return (
                        <Box
                          key={`head-${key}`}
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 12,
                            height: HEADER_ROW_HEIGHT,
                            boxSizing: 'border-box',
                            minWidth: 0,
                            px: { xs: 1, sm: 1.25 },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            overflow: 'hidden',
                            bgcolor: unassigned ? '#FFFAF3' : 'background.paper',
                            borderLeft: '1px solid #E5E7EB',
                            borderBottom: '1px solid #E5E7EB',
                          }}
                        >
                          {!isMobile && (
                            <Box
                              component="span"
                              sx={{
                                flex: 'none',
                                width: 32,
                                height: 32,
                                boxSizing: 'border-box',
                                borderRadius: '50%',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                bgcolor: unassigned ? 'transparent' : providerColor[provider.id]?.bg,
                                color: unassigned ? '#A85F1E' : providerColor[provider.id]?.fg,
                                border: unassigned ? '1.5px dashed #C9772B' : 0,
                              }}
                            >
                              {unassigned ? <PersonAddIcon sx={{ fontSize: 16 }} /> : providerInitials(provider.name)}
                            </Box>
                          )}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography noWrap sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 600, lineHeight: 1.3 }}>
                              {isMobile ? provider.name.split(' ')[0] : provider.name}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                              {unassigned
                                ? `${columnApts.length} to assign`
                                : `${columnApts.length} appt${columnApts.length === 1 ? '' : 's'}${bookedMinutes ? ` · ${formatDuration(bookedMinutes)} booked` : ''}`}
                            </Typography>
                          </Box>
                          {!unassigned && (
                            <Tooltip title={`Block time for ${provider.name}`}>
                              <IconButton
                                size="small"
                                aria-label={`Block time for ${provider.name}`}
                                onClick={() => {
                                  setTimeBlockVet(provider);
                                  setEditingTimeBlock(null);
                                  setIsTimeBlockDialogOpen(true);
                                }}
                                sx={{ flex: 'none', color: 'text.secondary' }}
                              >
                                <EventBusyIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })
                  : weekDays.map((day) => {
                      const dayKey = format(day, 'yyyy-MM-dd');
                      const isToday = dayKey === todayLocalISO();
                      // Count what this day's columns can show, so the header never counts a card it has no column for.
                      const shownIds = new Set(gridColumns.filter((c) => format(c.date, 'yyyy-MM-dd') === dayKey).map((c) => c.provider.id));
                      const count = (appointmentsByDay[dayKey] ?? []).filter((apt) => shownIds.has(apt.assignedVetId || 'unassigned')).length;
                      return (
                        <Box
                          key={`head-${dayKey}`}
                          component="button"
                          type="button"
                          onClick={() => openDay(day)}
                          aria-label={`Open ${format(day, 'EEEE, MMMM d')}, ${count} appointment${count === 1 ? '' : 's'}`}
                          sx={{
                            gridColumn: `span ${Math.max(providersPerDay, 1)}`,
                            position: 'sticky',
                            top: 0,
                            zIndex: 12,
                            height: HEADER_ROW_HEIGHT,
                            boxSizing: 'border-box',
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isWeekSplit ? 'flex-start' : 'center',
                            gap: 1,
                            px: 1.25,
                            font: 'inherit',
                            color: 'inherit',
                            textAlign: 'left',
                            cursor: 'pointer',
                            border: 0,
                            borderLeft: isWeekSplit ? '2px solid #D5D9DE' : '1px solid #E5E7EB',
                            borderBottom: isWeekSplit ? '1px solid #E5E7EB' : '1px solid #E5E7EB',
                            bgcolor: isToday ? '#fff6ec' : 'background.paper',
                            '&:hover': { bgcolor: isToday ? '#ffefdc' : '#F7F7F8' },
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              flex: 'none',
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              bgcolor: isToday ? 'primary.main' : 'transparent',
                              color: isToday ? 'primary.contrastText' : 'text.primary',
                            }}
                          >
                            {day.getDate()}
                          </Box>
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{format(day, 'EEE')}</Box>
                            <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                              {count} appt{count === 1 ? '' : 's'}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}

                {isWeekSplit &&
                  gridColumns.map(({ key, provider, dayStart }) => {
                    const color = providerColor[provider.id];
                    const unassigned = provider.id === 'unassigned';
                    return (
                      <Box
                        key={`sub-${key}`}
                        title={provider.name}
                        sx={{
                          position: 'sticky',
                          top: HEADER_ROW_HEIGHT,
                          zIndex: 12,
                          height: SUBHEADER_ROW_HEIGHT,
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'background.paper',
                          borderLeft: dayStart ? '2px solid #D5D9DE' : '1px solid #E5E7EB',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        <Box
                          component="span"
                          role="img"
                          aria-label={provider.name}
                          sx={{
                            width: 24,
                            height: 24,
                            boxSizing: 'border-box',
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            bgcolor: color?.bg ?? 'background.paper',
                            color: color?.fg ?? '#7A4314',
                            border: unassigned ? '1px dashed #C9772B' : 0,
                          }}
                        >
                          {unassigned ? '?' : providerInitials(provider.name)}
                        </Box>
                      </Box>
                    );
                  })}

                {/* Time labels, pinned to the left while the columns scroll sideways. Every sticky
                    cell here sits above the appointment cards (z 10) and below a dragged card (z 1000). */}
                <Box sx={{ position: 'sticky', left: 0, zIndex: 11, bgcolor: 'background.paper', borderRight: '1px solid #E5E7EB' }}>
                  {timeSlots.map((slot, index) => {
                    const hour24 = slot.hour;
                    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                    return (
                      <Box
                        key={`time-${index}`}
                        sx={{ height: `${slotHeight}px`, boxSizing: 'border-box', pr: 1, pt: 0.5, display: 'flex', justifyContent: 'flex-end' }}
                      >
                        {slot.isHourMark && (
                          <Typography sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem', fontWeight: 500, color: 'text.secondary', lineHeight: 1.2 }}>
                            {`${hour12} ${hour24 >= 12 ? 'PM' : 'AM'}`}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                  {showsToday && nowTop !== null && (
                    <Box
                      aria-hidden="true"
                      sx={{ position: 'absolute', right: 4, top: nowTop - 9, px: 0.6, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.68rem', fontWeight: 600, lineHeight: '18px' }}
                    >
                      {format(now, 'h:mm')}
                    </Box>
                  )}
                </Box>

                {gridColumns.map((col) => {
                  const dayKey = format(col.date, 'yyyy-MM-dd');
                  const columnAppointments = (appointmentsByDay[dayKey] ?? []).filter((apt) =>
                    col.provider.id === 'unassigned' ? !apt.assignedVetId : apt.assignedVetId === col.provider.id,
                  );
                  const isToday = dayKey === todayLocalISO();
                  const isTodayColumn = viewType === 'weekly' && isToday;
                  // A barn visit's horses share one window by design, so they render as one block
                  // that lists them; a barn booking with no horses gets the same block.
                  const byVisit = new Map<string, Appointment[]>();
                  columnAppointments.forEach((apt) => {
                    if (apt.visitGroupId) byVisit.set(apt.visitGroupId, [...(byVisit.get(apt.visitGroupId) ?? []), apt]);
                  });
                  const visits = Array.from(byVisit.entries());
                  const groupedIds = new Set(visits.flatMap(([, members]) => members.map((apt) => apt.id)));
                  const barnOnly = columnAppointments.filter(isBarnOnlyBooking);
                  const singles = columnAppointments.filter((apt) => !groupedIds.has(apt.id) && !isBarnOnlyBooking(apt));
                  return (
                    <Box
                      key={col.key}
                      sx={{
                        position: 'relative',
                        minWidth: 0,
                        height: `${timeSlots.length * slotHeight}px`,
                        borderLeft: viewType === 'weekly' && col.dayStart ? '2px solid #D5D9DE' : '1px solid #E5E7EB',
                        bgcolor: isTodayColumn ? '#FFFAF3' : col.provider.id === 'unassigned' && viewType === 'daily' ? '#FFFCF8' : 'transparent',
                      }}
                    >
                      {timeSlots.map((slot, slotIndex) => (
                        <DroppableTimeSlot
                          key={`${col.key}-bg-${slotIndex}`}
                          providerId={col.provider.id}
                          hour={slot.hour}
                          minute={slot.minute}
                          slotIndex={slotIndex}
                          timeInterval={timeInterval}
                          isHourMark={slot.isHourMark}
                          date={col.date}
                          onSlotClick={() => handleTimeSlotClick(col.date, slot.hour, slot.minute, col.provider.id)}
                          isDragging={isDragging}
                        />
                      ))}
                      {singles.map((appointment) => (
                        <DraggableAppointment
                          key={appointment.id}
                          appointment={appointment}
                          position={getAppointmentPosition(appointment)}
                          onOpenDetails={setSelectedAppointment}
                          typeColorMap={typeColorMap}
                          canReschedule={can.edit}
                          barn={barnById.get(resolveVisitBarnId(appointment) ?? '') ?? null}
                          homeBarn={barnById.get(resolveHomeBarnId(appointment) ?? '') ?? null}
                        />
                      ))}
                      {barnOnly.map((apt) => (
                        <DraggableVisitGroup
                          key={apt.id}
                          dragId={apt.id}
                          members={[apt]}
                          position={getAppointmentPosition(apt)}
                          onOpenDetails={setSelectedAppointment}
                          typeColorMap={typeColorMap}
                          canReschedule={can.edit}
                          barn={barnById.get(resolveVisitBarnId(apt) ?? '') ?? null}
                        />
                      ))}
                      {visits.map(([visitId, members]) => {
                        const sorted = [...members].sort((a, b) => a.date.localeCompare(b.date));
                        const startMs = new Date(sorted[0].date).getTime();
                        const endMs = Math.max(...sorted.map((apt) => new Date(apt.date).getTime() + (apt.duration || 60) * 60_000));
                        return (
                          <DraggableVisitGroup
                            key={`${visitId}-${col.key}`}
                            dragId={`visit:${visitId}|${col.provider.id}|${dayKey}`}
                            members={sorted}
                            position={{ exactTop: getAppointmentPosition(sorted[0]).exactTop, height: minutesToPx((endMs - startMs) / 60_000) }}
                            onOpenDetails={setSelectedAppointment}
                            typeColorMap={typeColorMap}
                            canReschedule={can.edit}
                            barn={barnById.get(resolveVisitBarnId(sorted[0]) ?? '') ?? null}
                          />
                        );
                      })}
                      {getTimeBlocksForProvider(col.provider.id, dayKey).map((block: any) => renderTimeBlock(block))}
                      {isToday && nowTop !== null && (
                        <Box
                          aria-hidden="true"
                          sx={{ position: 'absolute', left: 0, right: 0, top: nowTop, height: 0, borderTop: '2px solid #C9772B', zIndex: 9, pointerEvents: 'none' }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

          {/* Empty State */}
          {filteredAppointments.length === 0 && !showEquipment && (
            <Box 
              sx={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 2,
                bgcolor: 'background.paper',
                p: 3,
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchTerm ? `Nothing matches "${search.trim()}"` : 'No appointments scheduled'}
              </Typography>
              {searchTerm ? (
                <Button variant="outlined" onClick={() => setSearch('')} sx={{ textTransform: 'none' }}>
                  Clear search
                </Button>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedProviderId === 'unassigned'
                      ? `for Unassigned appointments ${
                          viewType === 'weekly' ? 'this week' : 
                          viewType === 'monthly' ? `in ${format(currentDate, 'MMMM yyyy')}` :
                          `on ${format(currentDate, 'EEE, MMMM dd, yyyy')}`
                        }`
                      : selectedProviderId 
                        ? `for ${veterinarians.find(v => v.id === selectedProviderId)?.name} ${
                            viewType === 'weekly' ? 'this week' : 
                            viewType === 'monthly' ? `in ${format(currentDate, 'MMMM yyyy')}` :
                            `on ${format(currentDate, 'EEE, MMMM dd, yyyy')}`
                          }`
                        : `${
                            viewType === 'weekly' ? 'this week' : 
                            viewType === 'monthly' ? `in ${format(currentDate, 'MMMM yyyy')}` :
                            `for ${format(currentDate, 'EEE, MMMM dd, yyyy')}`
                          }`
                    }
                  </Typography>
                  {can.add && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setSelectedDialogDate(new Date(currentDate));
                        setSelectedDuration(60);
                        setIsAddDialogOpen(true);
                      }}
                    >
                      Book First Appointment
                    </Button>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Paper>
      </Box>

      {/* Legend: the appointment types on screen in Day and Week, the vets in Month. */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px 18px',
          px: { xs: 1.5, sm: 2.5 },
          py: 1.25,
          fontSize: '0.78rem',
          color: 'text.secondary',
        }}
      >
        {viewType === 'monthly' || showEquipment
          ? (showEquipment
              ? [...veterinarians, ...(equipmentCounts.unassigned ? [UNASSIGNED_PROVIDER] : [])]
              : monthProviders
            ).map((provider) => (
              <Box key={provider.id} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    width: 20,
                    height: 20,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    bgcolor: providerColor[provider.id]?.bg ?? 'background.paper',
                    color: providerColor[provider.id]?.fg ?? '#7A4314',
                    border: provider.id === 'unassigned' ? '1px dashed #C9772B' : 0,
                  }}
                >
                  {provider.id === 'unassigned' ? '?' : providerInitials(provider.name)}
                </Box>
                {provider.name}
              </Box>
            ))
          : legendTypes.map((type) => (
              <Box key={type.label} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: type.fill, border: `1px solid ${type.line}` }} />
                {type.label}
              </Box>
            ))}
        {viewType !== 'monthly' && !showEquipment && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <WarehouseIcon sx={{ fontSize: 14 }} />
            Barn visit
          </Box>
        )}
      </Box>

      <AddAppointmentDialog
        open={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setSelectedDialogDate(null);
          setSelectedDuration(60);
          setSelectedVetForDialog('');
          setPreSelectedEquipmentId('');
        }}
        selectedDate={selectedDialogDate}
        selectedDuration={selectedDuration}
        preSelectedVetId={selectedVetForDialog}
        preSelectedEquipmentId={preSelectedEquipmentId}
        canAddOwner={can.newOwner}
        canAddPatient={can.newPatient}
      />

      <CalendarSettings
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        prefs={schedulePrefs}
        onSave={(patch) => {
          if (patch.slotMinutes) setTimeInterval(patch.slotMinutes);
          saveSchedulePrefs(patch);
        }}
        types={typeOptions}
      />

      {/* The appointment panel (canvas "Appointment panel" and "Edit appointment"). */}
      {selectedAppointment && (
        <AppointmentPanel
          appointment={selectedAppointment}
          onClose={() => {
            setSelectedAppointment(null);
            handleCancelEdit();
          }}
          visitMembers={
            selectedAppointment.visitGroupId
              ? appointments
                  .filter((a) => a.visitGroupId === selectedAppointment.visitGroupId && (a.id === selectedAppointment.id || !isCancelled(a.status)))
                  .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
              : []
          }
          onOpenAppointment={(next) => {
            handleCancelEdit();
            setSelectedAppointment(next);
          }}
          barn={barnById.get(resolveVisitBarnId(selectedAppointment) ?? '') ?? null}
          homeBarn={barnById.get(resolveHomeBarnId(selectedAppointment) ?? '') ?? null}
          typeColor={getAppointmentColor(selectedAppointment, typeColorMap)}
          formatInstant={formatInstant}
          can={{
            edit: can.edit,
            remove: can.remove,
            generateInvoice: can.generateInvoice,
            openPatientChart: can.openPatientChart,
          }}
          hasInvoice={hasInvoice}
          onGenerateInvoice={handleGenerateInvoice}
          onStatusChange={handleStatusChange}
          onEdit={handleEditAppointment}
          onDelete={() => setIsDeleteDialogOpen(true)}
          onCancelAppointment={() => setIsCancelConfirmOpen(true)}
          editor={
            isEditMode ? (
              <AppointmentEditor
                appointment={selectedAppointment}
                form={editFormData}
                onChange={(patch) => setEditFormData((f) => ({ ...f, ...patch }))}
                providers={panelProviders}
                types={typeOptions}
                barn={barnById.get(resolveVisitBarnId(selectedAppointment) ?? '') ?? null}
                visitHorseIds={liveVisitHorses(selectedAppointment).map((a) => a.id)}
                barnChoices={
                  isBarnOnlyBooking(selectedAppointment)
                    ? barns.filter((b) => !b.isArchived || b.id === selectedAppointment.barnId)
                    : undefined
                }
                offline={isOffline}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
                equipment={
                  /* Availability is server-checked; offline the edit saves without touching
                     reservations. Every callback marks equipment dirty: the save payload includes
                     `equipment` only then (a replica-hydrated row carries no schedules, so an
                     unconditional list would wipe real reservations server-side). */
                  <EquipmentWindowsPicker
                    items={editEquipmentItems}
                    selectedIds={editEquipmentIds}
                    onSelectedIdsChange={(ids) => {
                      setEditEquipmentDirty(true);
                      setEditEquipmentIds(ids);
                    }}
                    windows={editEquipmentWindows}
                    onWindowsChange={(w) => {
                      setEditEquipmentDirty(true);
                      setEditEquipmentWindows(w);
                    }}
                    allDayById={editEquipmentAllDay}
                    onAllDayChange={(m) => {
                      setEditEquipmentDirty(true);
                      setEditEquipmentAllDay(m);
                    }}
                    disabled={isOffline}
                    helperText={isOffline ? 'Equipment changes need a connection. Reservations stay as they are.' : undefined}
                    appointmentStart={editApptStart}
                    appointmentEnd={editApptEnd}
                  />
                }
              />
            ) : undefined
          }
        />
      )}

      <Dialog open={isCancelConfirmOpen} onClose={() => setIsCancelConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel this appointment?</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedAppointment?.visitGroupId
              ? `Only ${selectedAppointment.horse?.name ?? 'this horse'}'s appointment is cancelled. The rest of the barn visit stays booked. `
              : ''}
            It stays on record and in the cancellations report, and you can restore it from its status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCancelConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Keep it
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setIsCancelConfirmOpen(false);
              handleStatusChange('cancelled');
            }}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            Cancel appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete permanently?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This removes the appointment and its equipment bookings for good. To keep a record, cancel it instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAppointment} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Time Block Dialog */}
      <AddTimeBlockDialog
        open={isTimeBlockDialogOpen}
        onClose={() => {
          setIsTimeBlockDialogOpen(false);
          setTimeBlockVet(null);
          setEditingTimeBlock(null);
        }}
        selectedDate={currentDate}
        vet={timeBlockVet}
        editingBlock={editingTimeBlock}
      />

      {/* Time Block Detail Popover */}
      <Dialog
        open={!!selectedTimeBlock}
        onClose={() => setSelectedTimeBlock(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventBusyIcon sx={{ color: '#d32f2f' }} />
            <Typography variant="h6" component="span">
              {selectedTimeBlock?.vetId === null ? 'Clinic Closed' : 'Blocked Time'}
            </Typography>
          </Box>
          <IconButton onClick={() => setSelectedTimeBlock(null)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        {selectedTimeBlock && (
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {selectedTimeBlock.vetId === null ? (
                <Box sx={{ p: 1.5, backgroundColor: 'rgba(211, 47, 47, 0.06)', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600}>Clinic-wide closure</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Blocks every provider and unassigned bookings for this window.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                    {selectedTimeBlock.vet?.name?.split(' ').map((n: string) => n[0]).join('') || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{selectedTimeBlock.vet?.name}</Typography>
                    {selectedTimeBlock.vet?.professionalTitle && (
                      <Typography variant="caption" color="text.secondary">{selectedTimeBlock.vet.professionalTitle}</Typography>
                    )}
                  </Box>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography variant="body2">
                  {(() => {
                    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    return DateTime.fromISO(selectedTimeBlock.startTime, { zone: 'utc' })
                      .setZone(tz)
                      .toFormat('EEEE, MMMM d, yyyy');
                  })()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Time</Typography>
                <Typography variant="body2">
                  {(() => {
                    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const start = DateTime.fromISO(selectedTimeBlock.startTime, { zone: 'utc' }).setZone(tz).toFormat('h:mm a');
                    const end = DateTime.fromISO(selectedTimeBlock.endTime, { zone: 'utc' }).setZone(tz).toFormat('h:mm a');
                    return `${start} - ${end}`;
                  })()}
                </Typography>
              </Box>
              {selectedTimeBlock.reason && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Reason</Typography>
                  <Typography variant="body2">{selectedTimeBlock.reason}</Typography>
                </Box>
              )}
              {selectedTimeBlock.rangeGroupId && (() => {
                const siblings = timeBlocks.filter(
                  (b: any) => b.rangeGroupId === selectedTimeBlock.rangeGroupId,
                );
                return siblings.length > 1 ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Range</Typography>
                    <Typography variant="body2">
                      Part of a {siblings.length}-day range
                    </Typography>
                  </Box>
                ) : null;
              })()}
            </Box>
          </DialogContent>
        )}
        {/* Closures are OWNER/ADMIN-only to change, and online-only. */}
        {(selectedTimeBlock?.vetId !== null || canManageClosures) && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            startIcon={<EditIcon />}
            disabled={selectedTimeBlock?.vetId === null && isOffline}
            onClick={() => {
              const block = selectedTimeBlock;
              setSelectedTimeBlock(null);
              const vetObj = veterinarians.find((v: Veterinarian) => v.id === block.vetId) || block.vet || null;
              setTimeBlockVet(vetObj);
              setEditingTimeBlock(block);
              setIsTimeBlockDialogOpen(true);
            }}
            size="small"
          >
            Edit
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            disabled={selectedTimeBlock?.vetId === null && isOffline}
            onClick={() => setIsDeleteTimeBlockOpen(true)}
            size="small"
          >
            Delete
          </Button>
        </DialogActions>
        )}
      </Dialog>

      {/* Delete Time Block Confirmation */}
      <Dialog
        open={isDeleteTimeBlockOpen}
        onClose={() => setIsDeleteTimeBlockOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {selectedTimeBlock?.vetId === null ? 'Delete Clinic Closure' : 'Delete Time Block'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {selectedTimeBlock?.vetId === null ? 'closure' : 'time block'}?
            This action cannot be undone.
          </Typography>
          {(() => {
            const siblings = selectedTimeBlock?.rangeGroupId
              ? timeBlocks.filter((b: any) => b.rangeGroupId === selectedTimeBlock.rangeGroupId)
              : [];
            return siblings.length > 1 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This day is part of a {siblings.length}-day range — you can delete just this day or
                the whole range.
              </Typography>
            ) : null;
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteTimeBlockOpen(false)}>Cancel</Button>
          {(() => {
            const siblings = selectedTimeBlock?.rangeGroupId
              ? timeBlocks.filter((b: any) => b.rangeGroupId === selectedTimeBlock.rangeGroupId)
              : [];
            return siblings.length > 1 ? (
              <Button
                onClick={() => deleteTimeBlockMutation.mutate(siblings)}
                color="error"
                disabled={deleteTimeBlockMutation.isLoading}
              >
                Delete all {siblings.length} days
              </Button>
            ) : null;
          })()}
          <Button
            onClick={() => {
              if (selectedTimeBlock) {
                deleteTimeBlockMutation.mutate([selectedTimeBlock]);
              }
            }}
            color="error"
            variant="contained"
            disabled={deleteTimeBlockMutation.isLoading}
          >
            {selectedTimeBlock?.rangeGroupId ? 'Delete this day' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Equipment Schedule Confirmation Dialog */}
      <Dialog open={equipConfirmOpen} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Equipment Schedule</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This appointment has equipment booked. The times below have been shifted to match
            the new appointment time. Adjust if needed, then confirm.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {equipConfirmWindows.map((w, i) => (
              <Box
                key={w.itemId}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                <Typography variant="body2" fontWeight={600}>{w.name} — Occupied Window</Typography>
                <DateTimePicker
                  label="Start Time"
                  value={w.startTime}
                  onChange={(v) => v && setEquipConfirmWindows(prev =>
                    prev.map((item, idx) => idx === i ? { ...item, startTime: v } : item)
                  )}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
                <DateTimePicker
                  label="End Time"
                  value={w.endTime}
                  onChange={(v) => v && setEquipConfirmWindows(prev =>
                    prev.map((item, idx) => idx === i ? { ...item, endTime: v } : item)
                  )}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEquipConfirmOpen(false); setPendingUpdate(null); }} color="inherit">
            Cancel Move
          </Button>
          <Button onClick={handleEquipSkip} variant="outlined">
            Keep Original Times
          </Button>
          <Button onClick={handleEquipConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
    </DndContext>
  );
};

export default Appointments; 